import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import {
  FaUser,
  FaCar,
  FaPhone,
  FaUserTie,
  FaSearch,
  FaFilter,
  FaCalendarAlt,
  FaWallet,
  FaExclamationTriangle,
  FaCheckCircle,
  FaLock,
  FaClock,
  FaRoute,
  FaStar,
  FaTimes,
  FaEye,
  FaBuilding,
} from "react-icons/fa";
import socketService from "../../utils/socketService";
import StatusConfirmationModal from "../../components/shared/StatusConfirmationModal";
import Pagination from "../../components/shared/Pagination";
import {
  getPaymentMethodDisplay,
  getPaymentStatusDisplay,
  getPaymentMethodBadgeClass,
} from "../../utils/paymentDisplayUtils";
import { formatDateDDMMYYYY } from "../../utils/dateUtils";
import { useLocation } from "react-router-dom";

// Utility function to get booking amount from various possible fields
const getBookingAmount = (booking) => {
  return booking.totalAmount ||
         booking.fare ||
         booking.amount ||
         booking.finalAmount ||
         0;
};

// Driver Row Component with Wallet Balance Display
const DriverRow = ({
  driver,
  selectedBooking,
  assignDriver,
  user,
  API_URL,
}) => {
  const [walletInfo, setWalletInfo] = useState(null);
  const [loadingWallet, setLoadingWallet] = useState(true);

  useEffect(() => {
    const fetchWalletInfo = async () => {
      if (
        !selectedBooking?.paymentMethod ||
        selectedBooking.paymentMethod !== "ONLINE"
      ) {
        setLoadingWallet(false);
        return;
      }

      try {
        let config = {};
        if (user && user.token) {
          config = {
            headers: {
              Authorization: `Bearer ${user.token}`,
            },
          };
        }

        const driverId = driver._id || driver.id;

        // Get wallet and commission info
        const [walletResponse, commissionResponse] = await Promise.all([
          axios.get(`${API_URL}/wallet/driver/${driverId}`, config),
          axios.get(`${API_URL}/commission/rate?driverId=${driverId}`, config),
        ]);

        if (walletResponse.data.success && commissionResponse.data.success) {
          const walletData = walletResponse.data.data;
          const databaseBalance = walletData.balance || 0;

          // Calculate balance using the same smart logic
          const totalEarnings = walletData.totalEarnings || 0;
          const commissionPaid = walletData.calculatedCommission || walletData.totalCommissionPaid || 0;
          const calculatedBalance = totalEarnings - commissionPaid;

          // Use calculated balance if database balance is 0 or significantly different
          const shouldUseCalculated = databaseBalance === 0 || Math.abs(databaseBalance - calculatedBalance) > 100;
          const walletBalance = shouldUseCalculated ? calculatedBalance : databaseBalance;

          const commissionRate =
            commissionResponse.data.data.commissionRate || 10;
          const requiredCommission = Math.round(
            (getBookingAmount(selectedBooking) * commissionRate) / 100,
          );
          const driverEarnings =
            getBookingAmount(selectedBooking) - requiredCommission;
          // For online payments: Driver receives 90%, pending commission auto-deducted if negative balance
          const pendingCommission = walletBalance < 0 ? Math.abs(walletBalance) : 0;
          const effectiveEarnings = Math.max(0, driverEarnings - pendingCommission);
          const newBalance = walletBalance + driverEarnings;

          // For ONLINE payments: Driver always eligible (company receives money, driver gets 90%)
          // No upfront commission required for online payments
          const balanceAfterCommission = walletBalance + driverEarnings; // Driver will receive 90%
          const canAssign = true; // Online payments never block drivers

          setWalletInfo({
            walletBalance,
            commissionRate,
            requiredCommission,
            driverEarnings,
            balanceAfterCommission,
            canAssign,
          });
        }
      } catch (error) {
        console.error("Error fetching wallet info:", error);
      } finally {
        setLoadingWallet(false);
      }
    };

    fetchWalletInfo();
  }, [driver, selectedBooking, user, API_URL]);

  const formatCurrency = (amount) => {
    const numAmount = Number(amount);
    if (isNaN(numAmount)) {
      return "₹0";
    }
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numAmount);
  };

  const getWalletStatusColor = () => {
    if (!walletInfo) return "text-gray-500";
    if (walletInfo.newBalance >= 1000) return "text-green-600";
    if (walletInfo.newBalance >= 0) return "text-yellow-600";
    if (walletInfo.newBalance >= -1000) return "text-orange-600";
    return "text-red-600";
  };

  const getWalletStatusIcon = () => {
    if (!walletInfo) return <FaWallet className="w-4 h-4 text-gray-400" />;
    if (walletInfo.canAssign)
      return <FaCheckCircle className="w-4 h-4 text-green-500" />;
    return <FaExclamationTriangle className="w-4 h-4 text-red-500" />;
  };

  return (
    <tr key={driver.id || driver._id}>
      <td className="py-3 px-3">
        <div className="flex items-center">
          <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center mr-3">
            <FaUserTie className="text-gray-600 w-4 h-4" />
          </div>
          <div>
            <div className="font-medium text-gray-900">{driver.name} {driver.phone}</div>
            <div className="text-sm text-gray-500">{driver.email}</div>
          </div>
        </div>
      </td>
      <td className="py-3 px-3">
        <div className="text-sm text-gray-900">Available</div>
        {/* For Online Payments: Show simple commission info without wallet balance */}
        {selectedBooking?.paymentMethod === "ONLINE" && (
          <div className="mt-1">
            {loadingWallet ? (
              <div className="flex items-center text-xs text-gray-400">
                <div className="animate-spin w-3 h-3 border border-gray-300 rounded-full border-t-blue-500 mr-1"></div>
                Loading...
              </div>
            ) : walletInfo ? (
              <div className="text-xs text-green-600">
                <div className="flex items-center">
                  <FaCheckCircle className="w-3 h-3 mr-1" />
                  <span className="font-medium">Online Payment</span>
                </div>
                <div className="text-gray-500 mt-1">
                  <div>Driver gets: {formatCurrency(walletInfo.driverEarnings)} (90%)</div>
                  <div>Commission: {formatCurrency(walletInfo.requiredCommission)} (10%)</div>
                </div>
              </div>
            ) : (
              <div className="text-xs text-green-600">
                <FaCheckCircle className="w-3 h-3 mr-1 inline" />
                Online Payment Ready
              </div>
            )}
          </div>
        )}
      </td>
      <td className="py-3 px-3">
        <div className="text-sm">
          <div className="text-gray-900 font-medium">
            {driver.carType || driver.vehicleType || "Standard"}
          </div>
        </div>
      </td>
      <td className="py-3 px-3">
        <div className="flex flex-col space-y-1">
          <button
            onClick={() => assignDriver(driver.id)}
            className={`px-3 py-1 rounded-md text-sm transition-colors ${
              selectedBooking?.paymentMethod === "ONLINE" &&
              walletInfo &&
              !walletInfo.canAssign
                ? "bg-yellow-600 hover:bg-yellow-700 text-white"
                : "bg-blue-600 hover:bg-blue-700 text-white"
            }`}
            title={
              selectedBooking?.paymentMethod === "ONLINE" &&
              walletInfo &&
              !walletInfo.canAssign
                ? "Warning: Low wallet balance - click to proceed anyway"
                : "Assign this driver"
            }
          >
            {selectedBooking?.paymentMethod === "ONLINE" &&
            walletInfo &&
            !walletInfo.canAssign ? (
              <span className="flex items-center">
                <FaExclamationTriangle className="w-3 h-3 mr-1" />
                Assign
              </span>
            ) : (
              "Assign"
            )}
          </button>
          {selectedBooking?.paymentMethod === "ONLINE" && walletInfo && (
            <div className="text-xs text-center text-gray-500">
              Commission: {formatCurrency(walletInfo.requiredCommission)}
            </div>
          )}
        </div>
      </td>
    </tr>
  );
};

// Wallet Status Component for displaying wallet balance in driver list
const WalletStatus = ({ driverId, bookingAmount, user, API_URL, paymentMethod }) => {
  const [walletInfo, setWalletInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchWalletInfo = async () => {
      try {
        let config = {};
        if (user && user.token) {
          config = {
            headers: {
              Authorization: `Bearer ${user.token}`,
            },
          };
        }

        // Get wallet and commission info
        const [walletResponse, commissionResponse] = await Promise.all([
          axios.get(`${API_URL}/wallet/driver/${driverId}`, config),
          axios.get(`${API_URL}/commission/rate?driverId=${driverId}`, config),
        ]);

        if (walletResponse.data.success && commissionResponse.data.success) {
          const walletData = walletResponse.data.data;
          const databaseBalance = walletData.balance || 0;

          // Calculate balance using the same smart logic as other components
          const totalEarnings = walletData.totalEarnings || 0;
          const commissionPaid = walletData.calculatedCommission || walletData.totalCommissionPaid || 0;
          const calculatedBalance = totalEarnings - commissionPaid;

          // Use calculated balance if database balance is 0 or significantly different
          const shouldUseCalculated = databaseBalance === 0 || Math.abs(databaseBalance - calculatedBalance) > 100;
          const walletBalance = shouldUseCalculated ? calculatedBalance : databaseBalance;

          const commissionRate =
            commissionResponse.data.data.commissionRate || 10;
          const requiredCommission = Math.round(
            (bookingAmount * commissionRate) / 100,
          );
          const driverEarnings = bookingAmount - requiredCommission;
          // For online payments: Driver receives 90%, pending commission auto-deducted if negative balance
          const pendingCommission = walletBalance < 0 ? Math.abs(walletBalance) : 0;
          const effectiveEarnings = Math.max(0, driverEarnings - pendingCommission);
          const newBalance = walletBalance + driverEarnings;

          // For ONLINE payments: Driver always eligible (company receives money, driver gets 90%)
          // No upfront commission required for online payments
          const balanceAfterCommission = walletBalance + driverEarnings; // Driver will receive 90%
          const canAssign = true; // Online payments never block drivers

          setWalletInfo({
            walletBalance,
            databaseBalance,
            calculatedBalance,
            shouldUseCalculated,
            commissionRate,
            requiredCommission,
            driverEarnings,
            balanceAfterCommission,
            canAssign,
          });
        }
      } catch (error) {
        console.error("Error fetching wallet info:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchWalletInfo();
  }, [driverId, bookingAmount, user, API_URL]);

  const formatCurrency = (amount) => {
    const numAmount = Number(amount);
    if (isNaN(numAmount)) {
      return "₹0";
    }
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numAmount);
  };

  if (loading) {
    return (
      <div className="flex items-center text-xs text-gray-400 mt-1">
        <div className="animate-spin w-3 h-3 border border-gray-300 rounded-full border-t-blue-500 mr-1"></div>
        Checking wallet...
      </div>
    );
  }

  if (!walletInfo) {
    return (
      <div className="flex items-center text-xs text-gray-400 mt-1">
        <FaWallet className="w-3 h-3 mr-1" />
        Wallet: --
      </div>
    );
  }

  const getStatusColor = () => {
    // For online payments, always show positive status since drivers are never blocked
    if (walletInfo.canAssign) {
      return walletInfo.balanceAfterCommission >= 1000 ? "text-green-600" : "text-blue-600";
    }
    return "text-red-600"; // This should never happen for online payments
  };

  const getStatusIcon = () => {
    // For online payments, always show success icon since drivers are never blocked
    if (walletInfo.canAssign)
      return <FaCheckCircle className="w-3 h-3 text-green-500" />;
    return <FaLock className="w-3 h-3 text-red-500" />; // This should never happen for online payments
  };

  // For online payments, don't show wallet balance - just show commission info
  if (paymentMethod === "ONLINE") {
    return (
      <div className="mt-1">
        <div className="text-xs text-green-600">
          <div className="flex items-center">
            <FaCheckCircle className="w-3 h-3 mr-1" />
            <span className="font-medium">Online Payment</span>
          </div>
          <div className="text-gray-500 mt-1">
            <div>Driver gets: {formatCurrency(walletInfo.driverEarnings)} (90%)</div>
            <div>Commission: {formatCurrency(walletInfo.requiredCommission)} (10%)</div>
          </div>
        </div>
      </div>
    );
  }

  // For COD payments, show wallet balance as before
  return (
    <div className="mt-1">
      <div className={`flex items-center text-xs ${
        walletInfo.walletBalance < 0 ? 'text-red-600' : 'text-green-600'
      }`}>
        <FaWallet className="w-3 h-3 mr-1" />
        <span className="font-medium">
          Balance: {formatCurrency(walletInfo.walletBalance)}
        </span>
        {walletInfo.shouldUseCalculated && (
          <span className="ml-1 text-blue-600 text-xs">(Calc)</span>
        )}
      </div>
    </div>
  );
};

// Assign Button Component with wallet validation
const AssignButton = ({
  driver,
  selectedBooking,
  assignDriver,
  user,
  API_URL,
}) => {
  const [walletStatus, setWalletStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Only check wallet for online bookings
    if (selectedBooking?.paymentMethod !== "ONLINE") return;

    const checkWallet = async () => {
      setLoading(true);
      try {
        let config = {};
        if (user && user.token) {
          config = {
            headers: {
              Authorization: `Bearer ${user.token}`,
            },
          };
        }

        const driverId = driver._id || driver.id;
        const [walletResponse, commissionResponse] = await Promise.all([
          axios.get(`${API_URL}/wallet/driver/${driverId}`, config),
          axios.get(`${API_URL}/commission/rate?driverId=${driverId}`, config),
        ]);

        if (walletResponse.data.success && commissionResponse.data.success) {
          const walletData = walletResponse.data.data;
          const databaseBalance = walletData.balance || 0;

          // Calculate balance using the same smart logic
          const totalEarnings = walletData.totalEarnings || 0;
          const commissionPaid = walletData.calculatedCommission || walletData.totalCommissionPaid || 0;
          const calculatedBalance = totalEarnings - commissionPaid;

          // Use calculated balance if database balance is 0 or significantly different
          const shouldUseCalculated = databaseBalance === 0 || Math.abs(databaseBalance - calculatedBalance) > 100;
          const walletBalance = shouldUseCalculated ? calculatedBalance : databaseBalance;

          const commissionRate =
            commissionResponse.data.data.commissionRate || 10;
          const requiredCommission = Math.round(
            (getBookingAmount(selectedBooking) * commissionRate) / 100,
          );
          const driverEarnings =
            getBookingAmount(selectedBooking) - requiredCommission;
          // For online payments: Driver receives 90%, pending commission auto-deducted if negative balance
          const pendingCommission = walletBalance < 0 ? Math.abs(walletBalance) : 0;
          const effectiveEarnings = Math.max(0, driverEarnings - pendingCommission);
          const newBalance = walletBalance + driverEarnings;

          // For ONLINE payments: Driver always eligible (company receives money, driver gets 90%)
          // No upfront commission required for online payments
          const balanceAfterCommission = walletBalance + driverEarnings; // Driver will receive 90%
          const canAssign = true; // Online payments never block drivers

          setWalletStatus({
            canAssign,
            walletBalance,
            balanceAfterCommission,
            requiredCommission,
          });
        }
      } catch (error) {
        console.error("Error checking wallet:", error);
      } finally {
        setLoading(false);
      }
    };

    checkWallet();
  }, [driver, selectedBooking, user, API_URL]);

  const handleAssign = () => {
    assignDriver(driver.id || driver._id);
  };

  if (selectedBooking?.paymentMethod !== "ONLINE") {
    // For COD bookings, show normal assign button
    return (
      <button
        onClick={handleAssign}
        className="bg-blue-600 text-white px-3 py-1 rounded-md text-sm hover:bg-blue-700 transition-colors"
      >
        Assign
      </button>
    );
  }

  if (loading) {
    return (
      <button
        disabled
        className="bg-gray-400 text-white px-3 py-1 rounded-md text-sm cursor-not-allowed"
      >
        Checking...
      </button>
    );
  }

  if (!walletStatus) {
    return (
      <button
        onClick={handleAssign}
        className="bg-blue-600 text-white px-3 py-1 rounded-md text-sm hover:bg-blue-700 transition-colors"
      >
        Assign
      </button>
    );
  }

  // Show different button styles based on wallet status
  if (walletStatus.canAssign) {
    return (
      <button
        onClick={handleAssign}
        className="bg-green-600 text-white px-3 py-1 rounded-md text-sm hover:bg-green-700 transition-colors flex items-center"
        title={`Balance: ₹${walletStatus.walletBalance} | Commission: ₹${walletStatus.requiredCommission} | After: ₹${walletStatus.balanceAfterCommission}`}
      >
        <FaCheckCircle className="w-3 h-3 mr-1" />
        Assign
      </button>
    );
  } else {
    return (
      <button
        disabled
        className="bg-red-400 text-white px-3 py-1 rounded-md text-sm cursor-not-allowed flex items-center"
        title={`Cannot assign: This should not happen for online payments`}
      >
        <FaLock className="w-3 h-3 mr-1" />
        Blocked
      </button>
    );
  }
};

const Bookings = () => {
  const [bookings, setBookings] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [vendorCommissionMap, setVendorCommissionMap] = useState({});
  const [vendorWalletMap, setVendorWalletMap] = useState({});
  const [assignmentType, setAssignmentType] = useState("driver"); // "driver" or "vendor"
  // Import API URL from config
  const { API_URL } = require("../../config/apiConfig");
  const DRIVER_API_URL = `${API_URL}/driver`;
  const { user } = useSelector((state) => state.auth || {});

  // Currency formatter for this page (INR, no decimals)
  const formatCurrency = (amount) => {
    const num = Number(amount);
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Number.isFinite(num) ? num : 0);
  };

  const [totalBookings, setTotalBookings] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [cabTypes, setCabTypes] = useState({});
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showDriverModal, setShowDriverModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState("pending");
  // Read URL query param to allow linking from dashboard cards (e.g. /admin/bookings?status=assigned)
  const location = useLocation();

  // When URL query has a status param, initialize or update the status filter accordingly
  React.useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const statusParam = params.get('status');
      if (statusParam) {
        setStatusFilter(statusParam);
      }
    } catch (e) {
      // ignore
    }
  }, [location.search]);
  const [updatingBookings, setUpdatingBookings] = useState(new Set());
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [selectedRatingData, setSelectedRatingData] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Initialize socket connection
  useEffect(() => {
    // Connect to socket server
    const socket = socketService.connect();

    // Only set up listeners if socket connection is successful
    if (socket) {
      // Join admin room to receive updates
      socketService.joinAdminRoom();

      // Listen for booking updates (when a driver is assigned)
      socketService.onBookingUpdated((data) => {
        // Update the local state
        setBookings((prevBookings) => {
          // Find the booking that was updated
          const updatedBookings = prevBookings.map((booking) => {
            if (booking._id === data.booking._id) {
              // Create updated booking with driver information
              return {
                ...booking,
                status: "confirmed",
                driver: {
                  _id: data.driver._id,
                  name: data.driver.name,
                  phone: data.driver.phone,
                  vehicleType: data.driver.vehicleType,
                  vehicleModel: data.driver.vehicleModel,
                  vehicleNumber: data.driver.vehicleNumber,
                },
              };
            }
            return booking;
          });
          return updatedBookings;
        });

        toast.success(
          `Driver ${data.driver.name} assigned to booking successfully`,
        );

        // Also refresh the booking data to ensure consistency
        setTimeout(() => {
          refreshBookings();
        }, 500);
      });
    }

    // Clean up on component unmount
    return () => {
      socketService.disconnect();
    };
  }, []);

  // Refresh bookings function
  const refreshBookings = async () => {
    try {
      let config = {};
      if (user && user.token) {
        config = {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        };
      }

      const response = await axios.get(`${API_URL}/booking/admin/all`, config);
      if (response.data && response.data.success) {
        const formattedBookings = response.data.data.map((booking) => {
          // Use the same formatting logic as the main useEffect
          const buildLocation = (type) => {
            const locObj = type === "from" ? booking.pickupLocation : booking.dropLocation;
            const cityField = type === "from"
              ? booking.pickupCity || booking.fromCity
              : booking.dropCity || booking.toCity;

            const primaryField = type === "from" ? booking.from : booking.to;
            const secondaryField = type === "from" ? booking.pickupAddress : booking.dropAddress;

            if (locObj?.name) {
              return `${locObj.name}${locObj.state ? `, ${locObj.state}` : ""}`;
            }

            if (primaryField && typeof primaryField === "string" && primaryField.length < 50) {
              return primaryField;
            }

            return cityField || secondaryField || "Location not specified";
          };

          const cabTypeName = booking.cabType?.name || booking.cabType || booking.cabName || "Standard Cab";

          return {
            _id: booking._id,
            user: {
              _id: booking.user?._id || null,
              name: booking.passengerDetails?.name || booking.user?.name || "Anonymous",
              email: booking.passengerDetails?.email || booking.user?.email || "",
              phone: booking.passengerDetails?.phone || booking.user?.phone || "",
            },
            from: buildLocation("from"),
            to: buildLocation("to"),
            date: formatDateDDMMYYYY(new Date(booking.date || booking.pickupDate || booking.travelDate || Date.now())),
            serviceType: (() => {
              if (booking.serviceType && booking.serviceType !== "undefined") {
                return booking.serviceType;
              }
              if (booking.airport || booking.airportCode || booking.transferDirection) {
                return "airport";
              }
              if (booking.packageName || booking.rentalHours || booking.rentalKm) {
                return "rental";
              }
              return "outstation";
            })(),
            amount: booking.totalAmount || booking.fare || booking.amount || 0,
            cabType: cabTypeName,
            driver: booking.driver || null,
            status: booking.status || "pending",
            pickupAddress: booking.pickupAddress,
            dropAddress: booking.dropAddress,
            packageName: booking.packageName,
            rentalHours: booking.rentalHours,
            rentalKm: booking.rentalKm,
            viaCities: booking.viaCities || [],
            paymentMethod: booking.paymentMethod || "COD",
            tripType: booking.tripType || "oneWay",
          };
        });

        setBookings(formattedBookings);
        setTotalBookings(formattedBookings.length);
        console.log("Bookings refreshed successfully");
      }
    } catch (error) {
      console.error("Error refreshing bookings:", error);
    }
  };

  // Fetch bookings from the database
  useEffect(() => {
    const fetchBookings = async () => {
      setIsLoading(true);
      try {
        let config = {};

        // If user is authenticated, use the token for authorization
        if (user && user.token) {
          config = {
            headers: {
              Authorization: `Bearer ${user.token}`,
            },
          };
        }

        // Fetch bookings from the API
        const response = await axios.get(
          `${API_URL}/booking/admin/all`,
          config,
        );

        if (response.data && response.data.success) {
          // Transform the data to match our component structure
          const formattedBookings = response.data.data.map((booking) => {
            // Helper to build a readable location string from various possible fields
            const buildLocation = (type) => {
              const locObj =
                type === "from" ? booking.pickupLocation : booking.dropLocation;
              const cityField =
                type === "from"
                  ? booking.pickupCity || booking.fromCity
                  : booking.dropCity || booking.toCity;

              // Prioritize the from/to fields first as they contain proper location names
              const primaryField = type === "from" ? booking.from : booking.to;
              const secondaryField =
                type === "from" ? booking.pickupAddress : booking.dropAddress;

              // Prefer location object if it exists and has a name
              if (locObj?.name) {
                return `${locObj.name}${locObj.state ? `, ${locObj.state}` : ""}`;
              }

              // Check primary field (from/to) first - these usually contain proper location names
              if (
                primaryField &&
                primaryField.trim() &&
                primaryField !== "N/A" &&
                primaryField !== "Unknown" &&
                primaryField.length > 3 &&
                !primaryField.match(/^[a-z]{1,10}$/i)
              ) {
                return primaryField;
              }

              // Fallback to city field if valid
              if (cityField && cityField !== "N/A" && cityField.length > 2) {
                return cityField;
              }

              // Last resort: check secondary field (pickupAddress/dropAddress) if it looks like a real address
              if (
                secondaryField &&
                secondaryField.trim() &&
                secondaryField !== "N/A" &&
                secondaryField !== "Unknown" &&
                secondaryField.length > 3 &&
                !secondaryField.match(/^[a-z]{1,10}$/i)
              ) {
                return secondaryField;
              }

              return "Unknown Location";
            };

            const fromLocation = buildLocation("from");
            const toLocation = buildLocation("to");

            // Debug logging for address issues
            if (
              fromLocation === "Unknown Location" ||
              toLocation === "Unknown Location"
            ) {
              console.log("Address debug for booking:", booking.bookingId, {
                from: booking.from,
                to: booking.to,
                pickupAddress: booking.pickupAddress,
                dropAddress: booking.dropAddress,
                pickupLocation: booking.pickupLocation,
                dropLocation: booking.dropLocation,
              });
            }

            // Handle MongoDB cab type object
            const cabTypeName =
              booking.cabType?.name || booking.cabName || "Standard Cab";

            return {
              _id:
                booking._id ||
                booking.bookingId ||
                `BK${Math.floor(Math.random() * 10000)}`,
              bookingId: booking.bookingId,
              user: {
                _id: booking.user?._id || null,
                name: booking.userName || booking.user?.name || "Guest User",
                email:
                  booking.userEmail ||
                  booking.user?.email ||
                  "guest@example.com",
                phone: booking.userPhone || booking.user?.phone || booking.passengerDetails?.phone || "",
              },
              from: fromLocation,
              to: toLocation,
              // Preserve original detailed location data
              pickupLocation: booking.pickupLocation,
              dropLocation: booking.dropLocation,
              pickupAddress: booking.pickupAddress,
              dropAddress: booking.dropAddress,
              pickupAddressDetails: booking.pickupAddressDetails,
              dropAddressDetails: booking.dropAddressDetails,
              date: formatDateDDMMYYYY(new Date(
                booking.pickupDate ||
                  booking.travelDate ||
                  booking.date ||
                  Date.now(),
              )),
              // Add service type detection with proper fallback logic
              serviceType: (() => {
                if (booking.serviceType && booking.serviceType !== "undefined") {
                  return booking.serviceType;
                }
                // Check for airport indicators
                if (booking.airport || booking.airportCode || booking.transferDirection) {
                  return "airport";
                }
                // Check for rental indicators
                if (booking.packageName || booking.rentalHours || booking.rentalKm) {
                  return "rental";
                }
                return "outstation";
              })(),
              packageName: booking.packageName,
              rentalHours: booking.rentalHours,
              rentalKm: booking.rentalKm,
              extraFarePerHour: booking.extraFarePerHour,
              rentalPackageId: booking.rentalPackageId,
              paymentMethod: booking.paymentMethod,
              paymentId: booking.paymentId,
              status: booking.status || "pending",
              amount:
                booking.totalAmount || booking.fare || booking.amount || 0,
              cabType: cabTypeName,
              driver: booking.driver || null,

              // Trip type information (One Way vs Round Trip vs Multi-City)
              tripType: (() => {
                // Debug logging for trip type detection
                console.log("Admin Bookings - Trip Type Debug for booking:", booking.bookingId || booking._id, {
                  tripType: booking.tripType,
                  journeyType: booking.journeyType,
                  returnDate: booking.returnDate,
                  returnTime: booking.returnTime,
                  return_date: booking.return_date,
                  return_time: booking.return_time,
                  type: booking.type,
                  viaCities: booking.viaCities
                });

                // Check if booking has explicit journey type (primary field)
                if (booking.journeyType && booking.journeyType !== "undefined") {
                  if (booking.journeyType === "roundTrip") {
                    console.log("Using journeyType as Round Trip:", booking.journeyType);
                    return "roundTrip";
                  }
                  if (booking.journeyType === "oneWay") {
                    console.log("Using journeyType as One Way:", booking.journeyType);
                    return "oneWay";
                  }
                  // Support legacy formats if any exist
                  if (booking.journeyType === "round_trip" || booking.journeyType === "round-trip") {
                    console.log("Using legacy journeyType as Round Trip:", booking.journeyType);
                    return "roundTrip";
                  }
                  if (booking.journeyType === "one_way" || booking.journeyType === "one-way") {
                    console.log("Using legacy journeyType as One Way:", booking.journeyType);
                    return "oneWay";
                  }
                }

                // Check if booking has explicit trip type (fallback)
                if (booking.tripType && booking.tripType !== "undefined") {
                  console.log("Using explicit tripType:", booking.tripType);
                  return booking.tripType;
                }

                // Check for round trip indicators (multiple possible field names)
                if (booking.returnDate || booking.returnTime ||
                    booking.return_date || booking.return_time) {
                  console.log("Detected as Round Trip based on return date/time");
                  return "roundTrip";
                }

                // Check for via cities (multi-city trip)
                if (booking.viaCities && booking.viaCities.length > 0) {
                  console.log("Detected as Multi-City based on viaCities");
                  return "multiCity";
                }

                // Check for journey type patterns that indicate round trip
                if (booking.type === "round-trip" || booking.type === "roundTrip") {
                  console.log("Detected as Round Trip based on type field");
                  return "roundTrip";
                }

                // Default to one way
                console.log("Defaulting to One Way");
                return "oneWay";
              })(),

              // Via cities information
              viaCities: booking.viaCities || [],
              returnDate: booking.returnDate || null,
              returnTime: booking.returnTime || null,
            };
          });

          setBookings(formattedBookings);
          setTotalBookings(response.data.total || formattedBookings.length);
        } else {
          // If API call fails or returns no data, try localStorage as fallback
          tryLocalStorageFallback();
        }
      } catch (error) {
        console.error("Error fetching bookings:", error);
        // If API call fails, try localStorage as fallback
        tryLocalStorageFallback();
      } finally {
        setIsLoading(false);
      }
    };

    // Fallback to localStorage when API fails
    const tryLocalStorageFallback = () => {
      try {
        // Try to get all bookings from localStorage
        const userBookings = JSON.parse(
          localStorage.getItem("userBookings") || "[]",
        );
        if (userBookings.length > 0) {
          const formattedBookings = userBookings.map((booking) => ({
            _id:
              booking.id ||
              booking.bookingId ||
              `BK${Math.floor(Math.random() * 10000)}`,
            user: {
              _id: booking.user?._id || null,
              name: booking.userName || booking.user?.name || "Guest User",
              email: booking.userEmail || booking.user?.email || "guest@example.com",
              phone: booking.userPhone || booking.user?.phone || booking.passengerDetails?.phone || "",
            },
            from:
              booking.from ||
              booking.pickupLocation ||
              booking.fromCity ||
              "",
            to: booking.to || booking.dropLocation || booking.toCity || "",
            // Preserve detailed location data
            pickupLocation: booking.pickupLocation,
            dropLocation: booking.dropLocation,
            pickupAddress: booking.pickupAddress,
            dropAddress: booking.dropAddress,
            pickupAddressDetails: booking.pickupAddressDetails,
            dropAddressDetails: booking.dropAddressDetails,
            date: formatDateDDMMYYYY(new Date(
              booking.date ||
                booking.pickupDate ||
                booking.travelDate ||
                Date.now(),
            )),
            // Add service type detection with proper fallback logic
            serviceType: (() => {
              if (booking.serviceType && booking.serviceType !== "undefined") {
                return booking.serviceType;
              }
              // Check for airport indicators
              if (booking.airport || booking.airportCode || booking.transferDirection) {
                return "airport";
              }
              // Check for rental indicators
              if (booking.packageName || booking.rentalHours || booking.rentalKm) {
                return "rental";
              }
              return "outstation";
            })(),
            packageName: booking.packageName,
            rentalHours: booking.rentalHours,
            rentalKm: booking.rentalKm,
            extraFarePerHour: booking.extraFarePerHour,
            rentalPackageId: booking.rentalPackageId,
            paymentMethod: booking.paymentMethod,
            paymentId: booking.paymentId,
            status: booking.status || "pending",
            amount: booking.amount || booking.totalAmount || booking.fare || 0,
            cabType: booking.cabType || booking.cabName || "Standard Cab",
            driver: booking.driver || null,

            // Trip type information
            tripType: (() => {
              if (booking.tripType && booking.tripType !== "undefined") {
                return booking.tripType;
              }
              // Check for round trip indicators (multiple possible field names)
              if (booking.returnDate || booking.returnTime ||
                  booking.return_date || booking.return_time ||
                  booking.journeyType === "round_trip" ||
                  booking.journeyType === "roundTrip" ||
                  booking.journeyType === "round-trip" ||
                  booking.type === "round-trip" || booking.type === "roundTrip") {
                return "roundTrip";
              }
              if (booking.viaCities && booking.viaCities.length > 0) {
                return "multiCity";
              }
              return "oneWay";
            })(),
            viaCities: booking.viaCities || [],
            returnDate: booking.returnDate || null,
            returnTime: booking.returnTime || null,
          }));

          setBookings(formattedBookings);
          setTotalBookings(formattedBookings.length);
        } else {
          // If no bookings in localStorage, create demo data
          createDemoData();
        }
      } catch (error) {
        console.error("Error using localStorage fallback:", error);
        createDemoData();
      }
    };

    // No demo bookings: ensure page shows only real data or empty state when API/localStorage fail
    const createDemoData = () => {
      setBookings([]);
      setTotalBookings(0);
    };

    fetchBookings();
  }, [user, API_URL, page, limit, statusFilter]);

  // Fetch all available drivers
  useEffect(() => {
    const fetchDrivers = async () => {
      // Only fetch if needed
      if (drivers.length === 0) {
        try {
          const config =
            user && user.token
              ? {
                  headers: {
                    Authorization: `Bearer ${user.token}`,
                  },
                }
              : {};

          // Fetch both drivers and cab types to map them properly
          // First fetch drivers
          const driversResponse = await axios.get(
            `${DRIVER_API_URL}/available`,
            config,
          );
          const availableDrivers = driversResponse.data.data || [];

          // Then fetch cab types to map IDs to names
          let cabTypesMap = {};
          let cabTypesFetched = false;

          // Step 1: Try the new dedicated cab type endpoint
          try {
            const cabTypesResponse = await axios.get(
              `${API_URL}/cabtype/all`,
              config,
            );
            if (cabTypesResponse.data && cabTypesResponse.data.data) {
              cabTypesResponse.data.data.forEach((cabType) => {
                cabTypesMap[cabType._id] = cabType.name;
              });
              cabTypesFetched = true;
            }
          } catch (error) {
            console.error("Error fetching from /cabtype/all endpoint:", error);
          }

          // Step 3: Last resort - try the /cab endpoint
          if (!cabTypesFetched) {
            try {
              const fallbackResponse = await axios.get(
                `${API_URL}/cab`,
                config,
              );
              if (fallbackResponse.data && fallbackResponse.data.data) {
                fallbackResponse.data.data.forEach((cabType) => {
                  cabTypesMap[cabType._id] = cabType.name;
                });
              }
            } catch (fallbackError) {
              console.error(
                "Error fetching from all cab type endpoints:",
                fallbackError,
              );
              // Create a default mapping if all else fails
            }
          }

          setCabTypes(cabTypesMap);

          // Filter to only admin drivers (exclude vendor drivers)
          const adminDrivers = availableDrivers.filter((driver) => {
            // Exclude vendor drivers - only include admin drivers
            return !driver.vendorId && !driver.vendor;
          });

          // Format the admin drivers data
          const formattedDrivers = adminDrivers.map((driver) => {
            // Get the vehicle type ID and name from the driver record
            let vehicleTypeId = null;
            let vehicleTypeName = "Unknown";

            // Handle different possible driver data structures
            if (driver.vehicleType) {
              if (typeof driver.vehicleType === "string") {
                // ID reference directly
                vehicleTypeId = driver.vehicleType;
              } else if (driver.vehicleType._id) {
                // Populated mongoose object
                vehicleTypeId = driver.vehicleType._id;
                // If the object has a name, use it directly
                if (driver.vehicleType.name) {
                  vehicleTypeName = driver.vehicleType.name;
                }
              }
            }

            // Map driver data to our component format
            return {
              id: driver._id,
              name: driver.name || "Unknown Driver",
              phone: driver.phone || "",
              email: driver.email || "",
              status: driver.status || "available",
              carType: driver.vehicle?.model || vehicleTypeName || "Standard",
              carNumber: driver.vehicle?.registrationNumber || "",
              vehicleTypeId: vehicleTypeId,
              location: driver.location || "",
              rating: driver.rating || 4.5,
            };
          });

          // Update state with the formatted driver data
          setDrivers(formattedDrivers);
        } catch (error) {
          console.error("Error fetching drivers:", error);
          // Set some demo drivers if API fails
          if (drivers.length === 0) {
            setDrivers([
              {
                id: "D001",
                name: "John Driver",
                phone: "9876543210",
                status: "available",
                carType: "Sedan",
                carNumber: "DL-01-AB-1234",
                rating: 4.7,
              },
              {
                id: "D002",
                name: "Mary Wheels",
                phone: "8765432109",
                status: "available",
                carType: "SUV",
                carNumber: "DL-02-CD-5678",
                rating: 4.9,
              },
              {
                id: "D003",
                name: "Sam Speed",
                phone: "7654321098",
                status: "available",
                carType: "Hatchback",
                carNumber: "DL-03-EF-9012",
                rating: 4.5,
              },
            ]);
          }
        }
      } else {
        // Drivers already loaded, skipping fetch
      }
    };

    fetchDrivers();
  }, [drivers.length, user, DRIVER_API_URL, API_URL]);

  // Fetch all available vendors
  useEffect(() => {
    const fetchVendors = async () => {
      if (vendors.length === 0) {
        try {
          const config = user && user.token
            ? {
                headers: {
                  Authorization: `Bearer ${user.token}`,
                },
              }
            : {};

          const vendorsResponse = await axios.get(
            `${API_URL}/admin/vendors?page=1&limit=100`,
            config
          );

          console.log('Vendor API response:', vendorsResponse.data);

          if (vendorsResponse.data && (vendorsResponse.data.success || vendorsResponse.data.status)) {
            if (vendorsResponse.data.data && vendorsResponse.data.data.vendors) {
              console.log('Setting vendors from data.vendors:', vendorsResponse.data.data.vendors);
              setVendors(vendorsResponse.data.data.vendors);
            } else if (vendorsResponse.data.vendors) {
              console.log('Setting vendors from direct vendors field:', vendorsResponse.data.vendors);
              setVendors(vendorsResponse.data.vendors);
            } else if (vendorsResponse.data.data && Array.isArray(vendorsResponse.data.data)) {
              console.log('Setting vendors from data array directly:', vendorsResponse.data.data);
              setVendors(vendorsResponse.data.data);
            } else {
              console.log('No vendors found in response structure');
              setVendors([]);
            }
          } else {
            console.log('Vendor API response not successful');
            setVendors([]);
          }
        } catch (error) {
          console.error("Error fetching vendors:", error);
          setVendors([]);
        }
      }
    };

    fetchVendors();
  }, [vendors.length, user, API_URL]);

  // Fetch commission rate for each vendor (admin view) and precise wallet totals via admin drivers API
  useEffect(() => {
    const fetchVendorAuxData = async () => {
      if (!vendors || vendors.length === 0) return;
      try {
        const storedUser = JSON.parse(localStorage.getItem('user'));
        const token = storedUser?.token || storedUser?.data?.token;
        if (!token) return;
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const commissionUpdates = {};
        const walletUpdates = {};
        await Promise.all(
          vendors.map(async (vendor) => {
            if (!vendor || !vendor._id) return;
            try {
              if (!vendorCommissionMap[vendor._id]) {
                const res = await axios.get(`${API_URL}/commission/admin/vendor/${vendor._id}/settings`, config);
                if (res.data && (res.data.success || res.data.status) && res.data.data) {
                  const eff = res.data.data.commissionSettings?.effectiveRate ??
                              res.data.data.commissionStructure?.vendorRate ?? null;
                  commissionUpdates[vendor._id] = { effectiveRate: eff };
                }
              }
            } catch (_) {}

            try {
              if (vendorWalletMap[vendor._id] == null) {
                const sumRes = await axios.get(`${API_URL}/admin/vendors/${vendor._id}/wallet/summary`, config);
                const total = sumRes.data?.data?.totalCommissionEarned ?? 0;
                walletUpdates[vendor._id] = Number(total) || 0;
              }
            } catch (_) {}
          })
        );
        if (Object.keys(commissionUpdates).length > 0) {
          setVendorCommissionMap((prev) => ({ ...prev, ...commissionUpdates }));
        }
        if (Object.keys(walletUpdates).length > 0) {
          setVendorWalletMap((prev) => ({ ...prev, ...walletUpdates }));
        }
      } catch (_) {}
    };
    fetchVendorAuxData();
  }, [vendors, API_URL]);

  // Manual vendor fetching function
  const fetchVendorsManually = async () => {
    try {
      console.log('Manually fetching vendors...');
      // Get token from stored user object
      const storedUser = JSON.parse(localStorage.getItem('user'));
      const token = storedUser?.token || storedUser?.data?.token;

      if (!token) {
        console.error('No token available for vendor fetching');
        return;
      }

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      const vendorsResponse = await axios.get(
        `${API_URL}/admin/vendors?page=1&limit=100`,
        config
      );

      console.log('Manual vendor API response:', vendorsResponse.data);

      if (vendorsResponse.data && (vendorsResponse.data.success || vendorsResponse.data.status)) {
        if (vendorsResponse.data.data && vendorsResponse.data.data.vendors) {
          console.log('Setting vendors from data.vendors:', vendorsResponse.data.data.vendors);
          setVendors(vendorsResponse.data.data.vendors);
        } else if (vendorsResponse.data.vendors) {
          console.log('Setting vendors from direct vendors field:', vendorsResponse.data.vendors);
          setVendors(vendorsResponse.data.vendors);
        } else if (vendorsResponse.data.data && Array.isArray(vendorsResponse.data.data)) {
          console.log('Setting vendors from data array directly:', vendorsResponse.data.data);
          setVendors(vendorsResponse.data.data);
        } else {
          console.log('No vendors found in response structure');
          setVendors([]);
        }
      } else {
        console.log('Vendor API response not successful');
        setVendors([]);
      }
    } catch (error) {
      console.error('Error manually fetching vendors:', error.response?.data || error.message);
      setVendors([]);
    }
  };

  // Handle status change click - opens confirmation modal
  const handleStatusChange = (bookingId, newStatus) => {
    const booking = bookings.find((b) => b._id === bookingId);
    if (!booking) return;

    setPendingStatusChange({ bookingId, newStatus });
    setSelectedBooking(booking);
    setShowStatusModal(true);
  };

  // Update booking status after confirmation
  const updateBookingStatus = async (bookingId, newStatus) => {
    // Find the booking to check current status
    const booking = bookings.find((b) => b._id === bookingId);
    if (!booking) {
      toast.error("Booking not found");
      return;
    }

    // Prevent changing status of completed or cancelled bookings
    if (booking.status === "completed" || booking.status === "cancelled") {
      toast.error(
        `Cannot change status of ${booking.status} booking. ` +
          `Final booking states cannot be modified.`,
      );
      return;
    }

    // Prevent going backwards in status progression
    if (booking.status === "confirmed" && newStatus === "pending") {
      toast.error(
        "Cannot change confirmed booking back to pending. " +
          "Status can only progress forward: pending → confirmed → completed."
      );
      return;
    }

    // Add booking to updating set
    setUpdatingBookings((prev) => new Set([...prev, bookingId]));

    try {
      let config = {};

      // If user is authenticated, use the token for authorization
      if (user && user.token) {
        config = {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        };
      }

      // Update the booking status on the server
      const response = await axios.put(
        `${API_URL}/booking/${bookingId}`,
        { status: newStatus },
        config,
      );

      if (response.data && response.data.success) {
        // Update local state with server response
        setBookings((prevBookings) =>
          prevBookings.map((booking) =>
            booking._id === bookingId
              ? { ...booking, status: newStatus, updatedAt: new Date() }
              : booking,
          ),
        );
        toast.success(
          `Booking status updated to ${newStatus.toUpperCase()} successfully!`,
        );
      } else {
        throw new Error(response.data?.message || "Update failed");
      }
    } catch (error) {
      console.error("Error updating booking status:", error);

      // Check for final status protection (completed/cancelled cannot be changed)
      if (
        error.response?.status === 403 &&
        (error.response?.data?.finalStatus ||
          error.response?.data?.driverControlled)
      ) {
        // Update booking data to reflect status protection and show modal
        const booking = bookings.find((b) => b._id === bookingId);
        if (booking) {
          setSelectedBooking({
            ...booking,
            statusChangedBy: error.response.data.statusChangedBy || "driver",
          });
          setShowStatusModal(true);
        }
        toast.error(error.response.data.message);
        return;
      }

      // Check other specific error types
      if (error.response?.status === 401) {
        toast.error("Authentication required. Please login again.");
      } else if (error.response?.status === 403) {
        toast.error("You do not have permission to update booking status.");
      } else if (error.response?.status === 404) {
        toast.error("Booking not found.");
      } else {
        // For development/demo: still update UI locally but show warning
        setBookings((prevBookings) =>
          prevBookings.map((booking) =>
            booking._id === bookingId
              ? { ...booking, status: newStatus }
              : booking,
          ),
        );
        toast.warning(
          `Status updated locally to ${newStatus.toUpperCase()}. Server connection issue: ${error.message}`,
        );
      }
    } finally {
      // Remove booking from updating set
      setUpdatingBookings((prev) => {
        const newSet = new Set(prev);
        newSet.delete(bookingId);
        return newSet;
      });
    }
  };

  // Close status confirmation modal
  const closeStatusModal = () => {
    setShowStatusModal(false);
    setPendingStatusChange(null);
    setSelectedBooking(null);
  };

  // Open driver assignment modal
  const openDriverAssignModal = (booking) => {
    console.log('Opening assignment modal for booking:', booking._id);
    console.log('Available drivers:', drivers.length);
    console.log('Available vendors:', vendors.length);
    console.log('Vendors data:', vendors);
    console.log('Current assignment type:', assignmentType);

    // Force refresh vendors when modal opens
    if (vendors.length === 0) {
      fetchVendorsManually();
    }

    // Reset assignment type to driver tab by default
    setAssignmentType("driver");
    setSelectedBooking(booking);
    setShowDriverModal(true);
  };

  // Close driver assignment modal
  const closeDriverAssignModal = () => {
    setSelectedBooking(null);
    setShowDriverModal(false);
  };

  // Check driver wallet balance and commission requirements
  const checkDriverWalletBalance = async (driverId, bookingAmount) => {
    try {
      console.log("🔍 Checking wallet for driver:", driverId);

      // Validate driver ID
      if (!driverId) {
        return { success: false, error: "Driver ID is required" };
      }

      let config = {};
      if (user && user.token) {
        config = {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        };
      } else {
        return { success: false, error: "Authentication token missing" };
      }

      console.log("📡 Making wallet API call to:", `${API_URL}/wallet/driver/${driverId}`);

      // First, verify driver exists
      try {
        const driverCheckResponse = await axios.get(
          `${API_URL}/driver/${driverId}`,
          config,
        );
        if (!driverCheckResponse.data.success) {
          return { success: false, error: "Driver not found in system" };
        }
        console.log("✅ Driver exists:", driverCheckResponse.data.data.name);
      } catch (driverError) {
        console.error("❌ Driver verification failed:", driverError);
        return { success: false, error: "Driver not found or inaccessible" };
      }

      // Get driver wallet details
      const walletResponse = await axios.get(
        `${API_URL}/wallet/driver/${driverId}`,
        config,
      );

      console.log("���� Wallet response:", walletResponse.data);

      // Get commission rate for this booking
      const commissionResponse = await axios.get(
        `${API_URL}/commission/rate?driverId=${driverId}`,
        config,
      );

      console.log("📊 Commission response:", commissionResponse.data);

      if (walletResponse.data.success && commissionResponse.data.success) {
        const walletBalance = Number(walletResponse.data.data.balance) || 0;
        const commissionRate = Number(commissionResponse.data.data.commissionRate) || 10;
        const bookingAmountNumber = Number(bookingAmount) || 0;
        const requiredCommission = Math.round(
          (bookingAmountNumber * commissionRate) / 100,
        );
        const driverEarnings = bookingAmountNumber - requiredCommission;
        // For online payments: Driver receives 90%, pending commission auto-deducted if negative balance
        const pendingCommission = walletBalance < 0 ? Math.abs(walletBalance) : 0;
        const effectiveEarnings = Math.max(0, driverEarnings - pendingCommission);
        const newBalance = walletBalance + driverEarnings;

        console.log("💳 Wallet calculation:", {
          currentBalance: walletBalance,
          bookingAmount: bookingAmountNumber,
          commissionRate,
          requiredCommission,
          driverEarnings,
          newBalance,
        });

        return {
          success: true,
          walletBalance,
          commissionRate,
          requiredCommission,
          driverEarnings,
          newBalance,
          canAssign: true, // Online payments are always assignable
        };
      }

      return { success: false, error: "Failed to fetch wallet details" };
    } catch (error) {
      console.error("❌ Error checking wallet balance:", error);
      console.error("❌ Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        config: error.config,
      });

      if (error.response?.status === 404) {
        return { success: false, error: "Driver wallet not found - driver may not exist" };
      }
      if (error.response?.status === 401) {
        return { success: false, error: "Authentication failed - please log in again" };
      }

      return { success: false, error: error.response?.data?.message || error.message };
    }
  };

  // Assign driver to booking
  const assignDriver = async (driverId) => {
    if (!selectedBooking) return;

    // Find the selected driver
    const selectedDriver = drivers.find(
      (driver) => driver.id === driverId || driver._id === driverId,
    );
    if (!selectedDriver) return;

    // Use MongoDB _id instead of id
    const mongoDbDriverId = selectedDriver._id || driverId;

    try {
      setIsLoading(true);
      let config = {};

      if (user && user.token) {
        config = {
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json",
          },
        };
      }

      // Determine driverSource and vendorId if available
      const driverSource = selectedDriver.vendorId || selectedDriver.vendor ? "vendor" : "admin";
      const vendorId = selectedDriver.vendorId || null;

      // Check if this is an online booking that requires commission check
      if (selectedBooking.paymentMethod === "ONLINE") {
        console.log("Checking wallet balance for online booking...");

        // Get booking amount from various possible fields
        const bookingAmount = getBookingAmount(selectedBooking);

        console.log("🔍 Booking amount fields:", {
          totalAmount: getBookingAmount(selectedBooking),
          fare: selectedBooking.fare,
          amount: selectedBooking.amount,
          finalAmount: selectedBooking.finalAmount,
          selectedAmount: bookingAmount
        });

        const walletCheck = await checkDriverWalletBalance(
          mongoDbDriverId,
          bookingAmount,
        );

        if (!walletCheck.success) {
          toast.error(`Failed to check driver wallet: ${walletCheck.error}`);
          return;
        }

        if (!walletCheck.canAssign) {
          // Show detailed warning with wallet information
          const confirmAssign = window.confirm(
            `⚠️ WALLET BALANCE WARNING ��️\n\n` +
              `Driver: ${selectedDriver.name}\n` +
              `Current Wallet Balance: ₹${walletCheck.walletBalance}\n` +
              `Booking Amount: ₹${bookingAmount}\n` +
              `Required Commission (${walletCheck.commissionRate}%): ��${walletCheck.requiredCommission}\n` +
              `Driver Earnings: ₹${walletCheck.driverEarnings}\n` +
              `Balance After Commission: ₹${walletCheck.newBalance}\n\n` +
              `⚠️ This driver's balance will be critically low (��${walletCheck.newBalance}).\n\n` +
              `Do you still want to assign this driver?`,
          );

          if (!confirmAssign) {
            toast.info("Driver assignment cancelled due to low wallet balance");
            return;
          }
        } else {
          // Show success info about wallet status
          toast.success(
            `✅ Wallet Check Passed\n` +
              `Driver: ${selectedDriver.name}\n` +
              `Current Balance: ₹${walletCheck.walletBalance}\n` +
              `Balance After Ride: ₹${walletCheck.newBalance}`,
            { autoClose: 3000 },
          );
        }
      }

      // Attempt to assign driver with retries
      let attempt = 0;
      const maxAttempts = 3;
      let apiSuccess = false;

      // Prepare payload and log it for debugging
      const payload = {
        bookingId: selectedBooking._id,
        driverId: mongoDbDriverId,
      };
      if (driverSource === "vendor") {
        payload.driverSource = "vendor";
        if (vendorId) payload.vendorId = vendorId;
      }

      console.log("Assign driver payload:", payload);

      while (attempt < maxAttempts && !apiSuccess) {
        try {
          // Make API call to assign driver using only the expected parameters
          const response = await axios.put(
            `${API_URL}/booking/assign-driver`,
            payload,
            config,
          );

          if (response.data && response.data.success) {
            apiSuccess = true;
          } else {
            // If the server responded with success:false, include response message
            const errMsg = response.data?.message || "Invalid API response format";
            throw new Error(errMsg);
          }
        } catch (apiError) {
          console.error(`Attempt ${attempt + 1}/${maxAttempts} failed:`, apiError);
          // Log server response if available
          if (apiError.response) {
            console.error("Server response:", apiError.response.data);
          }

          attempt++;

          if (attempt < maxAttempts) {
            // Wait before retrying (exponential backoff)
            await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
          } else {
            // On final failure, show server-provided message if present
            const serverMessage = apiError.response?.data?.message || apiError.message;
            toast.error(`Failed to assign driver: ${serverMessage}`);
          }
        }
      }

      // Update driver status to assigned
      setDrivers((prevDrivers) =>
        prevDrivers.map((driver) =>
          driver.id === driverId ? { ...driver, status: "assigned" } : driver,
        ),
      );

      // Update the booking with the assigned driver
      setBookings((prevBookings) =>
        prevBookings.map((booking) =>
          booking._id === selectedBooking._id
            ? {
                ...booking,
                status: "confirmed",
                driver: {
                  id: selectedDriver.id,
                  name: selectedDriver.name,
                  phone: selectedDriver.phone,
                  carNumber: selectedDriver.carNumber,
                  status: "assigned",
                },
              }
            : booking,
        ),
      );

      if (apiSuccess) {
        toast.success(
          `Driver ${selectedDriver.name} assigned to booking successfully`,
        );
        // Refresh bookings data to ensure confirmed bookings show up properly
        setTimeout(() => {
          refreshBookings();
        }, 1000);
      } else {
        // Server not reachable after retries — assignment applied locally only.
        // Suppress user-facing toast here to avoid confusion on the admin bookings page.
        console.warn("Unable to connect to server. Driver assigned locally only.");
      }
    } catch (error) {
      console.error("Error in assignment process:", error);
      toast.error(`Failed to assign driver. Please try again.`);
    } finally {
      setIsLoading(false);
      closeDriverAssignModal();
    }
  };

  // Assign vendor to booking
  const assignVendor = async (vendorId) => {
    if (!selectedBooking) return;

    // Find the selected vendor
    const selectedVendor = vendors.find(
      (vendor) => vendor._id === vendorId
    );
    if (!selectedVendor) return;

    try {
      setIsLoading(true);
      let config = {};

      if (user && user.token) {
        config = {
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json",
          },
        };
      }

      // Make API call to assign vendor
      const response = await axios.post(
        `${API_URL}/booking/${selectedBooking._id}/assign-vendor`,
        { vendorId: vendorId },
        config
      );

      if (response.data && response.data.success) {
        // Update the booking with the assigned vendor
        setBookings((prevBookings) =>
          prevBookings.map((booking) =>
            booking._id === selectedBooking._id
              ? {
                  ...booking,
                  status: "assigned_to_vendor",
                  vendor: {
                    _id: selectedVendor._id,
                    name: selectedVendor.fullName || selectedVendor.name,
                    email: selectedVendor.email,
                    phone: selectedVendor.phone,
                  },
                }
              : booking,
          ),
        );

        toast.success(
          `Booking assigned to vendor ${selectedVendor.fullName || selectedVendor.name} successfully. The vendor will assign a driver through their dashboard.`
        );

        // Refresh bookings data
        setTimeout(() => {
          refreshBookings();
        }, 1000);
      } else {
        toast.error("Failed to assign vendor. Please try again.");
      }
    } catch (error) {
      console.error("Error assigning vendor:", error);
      toast.error(`Failed to assign vendor: ${error.response?.data?.message || error.message}`);
    } finally {
      setIsLoading(false);
      closeDriverAssignModal();
    }
  };

  // Remove assigned driver
  const removeDriver = async (bookingId) => {
    // Find the booking
    const booking = bookings.find((b) => b._id === bookingId);
    if (!booking || !booking.driver) {
      toast.error("No driver assigned to this booking");
      return;
    }

    // Check if booking is in a final state that cannot be modified
    if (booking.status === "completed" || booking.status === "cancelled") {
      toast.error(
        `Cannot remove driver from ${booking.status} booking. ` +
          `Driver assignment is locked for final booking states.`,
      );
      return;
    }

    try {
      setIsLoading(true);
      let config = {};

      if (user && user.token) {
        config = {
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json",
          },
        };
      } else {
        toast.error("Authentication required");
        setIsLoading(false);
        return;
      }

      // Call the remove driver API
      const response = await axios.put(
        `${API_URL}/booking/${bookingId}/remove-driver`,
        {},
        config,
      );

      if (response.data && response.data.success) {
        console.log("Driver removed successfully via API");
      } else {
        throw new Error(response.data?.message || "Failed to remove driver");
      }

      // Update driver status back to available
      const driverToUpdate = drivers.find(
        (d) => d.phone === booking.driver.phone,
      );
      if (driverToUpdate) {
        setDrivers((prevDrivers) =>
          prevDrivers.map((driver) =>
            driver.id === driverToUpdate.id
              ? { ...driver, status: "available" }
              : driver,
          ),
        );
      }

      // Remove driver from booking
      setBookings((prevBookings) =>
        prevBookings.map((booking) =>
          booking._id === bookingId
            ? { ...booking, driver: null, status: "confirmed" }
            : booking,
        ),
      );

      toast.success(`Driver removed from booking successfully`);
    } catch (error) {
      console.error("Error removing driver:", error);

      // Handle specific error cases
      if (error.response?.status === 404) {
        toast.error("Booking not found");
      } else if (error.response?.status === 400) {
        toast.error(error.response.data?.message || "Cannot remove driver from this booking");
      } else if (error.response?.status === 401) {
        toast.error("Authentication required");
      } else if (error.response?.status === 403) {
        toast.error("You don't have permission to remove drivers");
      } else {
        toast.error(`Failed to remove driver: ${error.response?.data?.message || error.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to update local state when removing a driver
  const updateLocalDriverRemoval = (bookingId, booking) => {
    // Update driver status back to available
    const driverToUpdate = drivers.find(
      (d) => d.phone === booking.driver.phone,
    );
    if (driverToUpdate) {
      setDrivers((prevDrivers) =>
        prevDrivers.map((driver) =>
          driver.id === driverToUpdate.id
            ? { ...driver, status: "available" }
            : driver,
        ),
      );
    }

    // Remove driver from booking
    setBookings((prevBookings) =>
      prevBookings.map((booking) =>
        booking._id === bookingId ? { ...booking, driver: null } : booking,
      ),
    );
  };

  // View ratings function
  const viewRatings = async (booking) => {
    try {
      setSelectedRatingData({ loading: true });
      setShowRatingModal(true);

      let config = {};
      if (user && user.token) {
        config = {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        };
      }

      try {
        // Fetch ratings for this booking
        const response = await axios.get(
          `${API_URL}/booking/${booking._id}/ratings`,
          config,
        );

        if (response.data && response.data.success) {
          setSelectedRatingData({
            booking: booking,
            ratings: response.data.data,
            loading: false,
          });
        } else {
          // Show modal with no ratings message
          setSelectedRatingData({
            booking: booking,
            ratings: null,
            loading: false,
            message: "No ratings available for this booking",
          });
        }
      } catch (apiError) {
        console.error("API Error fetching ratings:", apiError);
        setSelectedRatingData({
          booking: booking,
          ratings: null,
          loading: false,
          error: apiError.response?.data?.message || "Failed to fetch ratings. Please try again.",
        });
      }
    } catch (error) {
      console.error("Error in viewRatings:", error);
      setSelectedRatingData({
        booking: booking,
        ratings: null,
        loading: false,
        error: "Failed to load ratings",
      });
    }
  };

  // Filter bookings based on search and status
  const allFilteredBookings = bookings.filter((booking) => {
    const matchesSearch =
      booking._id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.from.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.to.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = (() => {
      if (statusFilter === "all") return true;

      // For confirmed filter, only include confirmed status bookings
      if (statusFilter === "confirmed") {
        return booking.status === "confirmed";
      }

      // For assigned filter, include assigned bookings with drivers
      if (statusFilter === "assigned") {
        return booking.status === "assigned" && booking.driver;
      }
      if (statusFilter === "assigned_to_vendor") {
        return booking.status === "assigned_to_vendor";
      }

      return booking.status === statusFilter;
    })();

    return matchesSearch && matchesStatus;
  });

  // Calculate pagination
  const totalFilteredBookings = allFilteredBookings.length;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const filteredBookings = allFilteredBookings.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
          Booking Management
        </h1>
        <div className="flex items-center gap-4">
          <button
            onClick={refreshBookings}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            title="Refresh booking data"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
          <div className="text-sm text-gray-500">
            {formatDateDDMMYYYY(new Date())}
          </div>
        </div>
      </div>

      {/* Search and Active Filter Display */}
      <div className="bg-white rounded-xl shadow-md p-4 mb-6 border border-gray-100">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="w-full md:w-1/3">
            <label htmlFor="search" className="sr-only">
              Search
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg
                  className="h-5 w-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  ></path>
                </svg>
              </div>
              <input
                id="search"
                type="text"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Search bookings..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center bg-gray-100 rounded-lg px-4 py-2">
              <FaFilter className="text-gray-500 mr-2" />
              <span className="text-sm text-gray-600">Showing:</span>
              <span className="ml-2 text-sm font-semibold text-gray-800 capitalize">
                {statusFilter === "all" ? "All Bookings" : `${statusFilter} Bookings`}
              </span>
            </div>
            {statusFilter !== "all" && (
              <button
                onClick={() => setStatusFilter("all")}
                className="text-sm text-blue-600 hover:text-blue-800 underline flex items-center gap-1"
              >
                <FaTimes className="w-3 h-3" />
                Clear Filter
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Stats Summary - Clickable Filter Cards */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-8">
        <div
          className={`bg-white rounded-xl shadow-md p-5 border border-gray-100 hover:shadow-lg transition-all duration-300 cursor-pointer transform hover:scale-105 ${
            statusFilter === "all" ? "ring-2 ring-blue-500 bg-blue-50" : ""
          }`}
          onClick={() => setStatusFilter("all")}
        >
          <div className="flex items-center">
            <div className={`p-2 rounded-full mr-3 ${statusFilter === "all" ? "bg-blue-500" : "bg-blue-100"}`}>
              <FaCalendarAlt className={statusFilter === "all" ? "text-white" : "text-blue-600"} />
            </div>
            <div>
              <p className="text-gray-500 text-sm">Total Bookings</p>
              <p className="text-2xl font-bold">{totalBookings}</p>
            </div>
          </div>
          {statusFilter === "all" && (
            <div className="mt-2 text-xs text-blue-600 font-medium">Active Filter</div>
          )}
        </div>
        <div
          className={`bg-white rounded-xl shadow-md p-5 border border-gray-100 hover:shadow-lg transition-all duration-300 cursor-pointer transform hover:scale-105 ${
            statusFilter === "pending" ? "ring-2 ring-orange-500 bg-orange-50" : ""
          }`}
          onClick={() => setStatusFilter("pending")}
        >
          <div className="flex items-center">
            <div className={`p-2 rounded-full mr-3 ${statusFilter === "pending" ? "bg-orange-500" : "bg-orange-100"}`}>
              <FaClock className={statusFilter === "pending" ? "text-white" : "text-orange-600"} />
            </div>
            <div>
              <p className="text-gray-500 text-sm">Pending Bookings</p>
              <p className="text-2xl font-bold">
                {bookings.filter((b) => b.status === "pending").length}
              </p>
            </div>
          </div>
          {statusFilter === "pending" && (
            <div className="mt-2 text-xs text-orange-600 font-medium">Active Filter</div>
          )}
        </div>
        <div
          className={`bg-white rounded-xl shadow-md p-5 border border-gray-100 hover:shadow-lg transition-all duration-300 cursor-pointer transform hover:scale-105 ${
            statusFilter === "confirmed" ? "ring-2 ring-green-500 bg-green-50" : ""
          }`}
          onClick={() => setStatusFilter("confirmed")}
        >
          <div className="flex items-center">
            <div className={`p-2 rounded-full mr-3 ${statusFilter === "confirmed" ? "bg-green-500" : "bg-green-100"}`}>
              <FaCheckCircle className={statusFilter === "confirmed" ? "text-white" : "text-green-600"} />
            </div>
            <div>
              <p className="text-gray-500 text-sm">Confirmed Bookings</p>
              <p className="text-2xl font-bold">
                {bookings.filter((b) => b.status === "confirmed").length}
              </p>
              <p className="text-xs text-gray-400 mt-1">Payment confirmed</p>
            </div>
          </div>
          {statusFilter === "confirmed" && (
            <div className="mt-2 text-xs text-green-600 font-medium">Active Filter</div>
          )}
        </div>
        <div
          className={`bg-white rounded-xl shadow-md p-5 border border-gray-100 hover:shadow-lg transition-all duration-300 cursor-pointer transform hover:scale-105 ${
            statusFilter === "assigned" ? "ring-2 ring-teal-500 bg-teal-50" : ""
          }`}
          onClick={() => setStatusFilter("assigned")}
        >
          <div className="flex items-center">
            <div className={`p-2 rounded-full mr-3 ${statusFilter === "assigned" ? "bg-teal-500" : "bg-teal-100"}`}>
              <FaUserTie className={statusFilter === "assigned" ? "text-white" : "text-teal-600"} />
            </div>
            <div>
              <p className="text-gray-500 text-sm">Assigned Drivers</p>
              <p className="text-2xl font-bold">
                {bookings.filter((b) => b.status === "assigned" && b.driver).length}
              </p>
              <p className="text-xs text-gray-400 mt-1">With drivers assigned</p>
            </div>
          </div>
          {statusFilter === "assigned" && (
            <div className="mt-2 text-xs text-teal-600 font-medium">Active Filter</div>
          )}
        </div>

        <div
          className={`bg-white rounded-xl shadow-md p-5 border border-gray-100 hover:shadow-lg transition-all duration-300 cursor-pointer transform hover:scale-105 ${
            statusFilter === "assigned_to_vendor" ? "ring-2 ring-cyan-500 bg-cyan-50" : ""
          }`}
          onClick={() => setStatusFilter("assigned_to_vendor")}
        >
          <div className="flex items-center">
            <div className={`p-2 rounded-full mr-3 ${statusFilter === "assigned_to_vendor" ? "bg-cyan-500" : "bg-cyan-100"}`}>
              <FaUserTie className={statusFilter === "assigned_to_vendor" ? "text-white" : "text-cyan-600"} />
            </div>
            <div>
              <p className="text-gray-500 text-sm">Assigned to Vendor</p>
              <p className="text-2xl font-bold">
                {bookings.filter((b) => b.status === "assigned_to_vendor").length}
              </p>
              <p className="text-xs text-gray-400 mt-1">Awaiting vendor to assign driver</p>
            </div>
          </div>
          {statusFilter === "assigned_to_vendor" && (
            <div className="mt-2 text-xs text-cyan-600 font-medium">Active Filter</div>
          )}
        </div>
        <div
          className={`bg-white rounded-xl shadow-md p-5 border border-gray-100 hover:shadow-lg transition-all duration-300 cursor-pointer transform hover:scale-105 ${
            statusFilter === "completed" ? "ring-2 ring-purple-500 bg-purple-50" : ""
          }`}
          onClick={() => setStatusFilter("completed")}
        >
          <div className="flex items-center">
            <div className={`p-2 rounded-full mr-3 ${statusFilter === "completed" ? "bg-purple-500" : "bg-purple-100"}`}>
              <FaCheckCircle className={statusFilter === "completed" ? "text-white" : "text-purple-600"} />
            </div>
            <div>
              <p className="text-gray-500 text-sm">Completed Bookings</p>
              <p className="text-2xl font-bold">
                {bookings.filter((b) => b.status === "completed").length}
              </p>
            </div>
          </div>
          {statusFilter === "completed" && (
            <div className="mt-2 text-xs text-purple-600 font-medium">Active Filter</div>
          )}
        </div>
        <div
          className={`bg-white rounded-xl shadow-md p-5 border border-gray-100 hover:shadow-lg transition-all duration-300 cursor-pointer transform hover:scale-105 ${
            statusFilter === "cancelled" ? "ring-2 ring-red-500 bg-red-50" : ""
          }`}
          onClick={() => setStatusFilter("cancelled")}
        >
          <div className="flex items-center">
            <div className={`p-2 rounded-full mr-3 ${statusFilter === "cancelled" ? "bg-red-500" : "bg-red-100"}`}>
              <FaTimes className={statusFilter === "cancelled" ? "text-white" : "text-red-600"} />
            </div>
            <div>
              <p className="text-gray-500 text-sm">Cancelled Bookings</p>
              <p className="text-2xl font-bold">
                {bookings.filter((b) => b.status === "cancelled").length}
              </p>
            </div>
          </div>
          {statusFilter === "cancelled" && (
            <div className="mt-2 text-xs text-red-600 font-medium">Active Filter</div>
          )}
        </div>
      </div>

      {/* Bookings Table */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            <span className="ml-3 text-gray-600">Loading booking data...</span>
          </div>
        ) : (
          <div className="overflow-x-auto overflow-y-visible">
            <table className="admin-table">
              <colgroup>
                <col className="w-24" />
                <col className="w-32" />
                <col className="w-40" />
                <col className="w-48" />
                <col className="w-40" />
                <col className="w-44" />
                <col className="w-36" />
                <col className="w-40" />
                <col className="w-44" />
                <col className="w-36" />
              </colgroup>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Booking ID</th>
                  <th>Customer</th>
                  <th>Route & Pickup Details *</th>
                  <th>Drop Details *</th>
                  <th>Service & Package Details</th>
                  <th>Amount & Payment</th>
                  <th>Status</th>
                  <th>Driver & Car Type</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredBookings.length > 0 ? (
                  filteredBookings.map((booking) => (
                    <tr key={booking._id}>
                      <td>{booking.date}</td>
                      <td className="font-mono text-xs">{booking.bookingId || booking._id}</td>
                      <td>
                        <div>
                          <div className="font-medium">{booking.user.name}</div>
                          <div className="text-gray-500 text-sm">
                            {booking.user.email}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="space-y-2">
                          <div className="text-sm">
                            <span className="text-gray-500 font-medium">
                              {booking.serviceType === "rental" ? "Service:" : "Route:"}
                            </span>
                            <div className="font-medium text-blue-600 mt-1">
                              {booking.serviceType === "rental" ? (
                                <div>
                                  <div className="text-green-600 font-semibold">
                                    📦 {booking.packageName ||
                                        `${booking.rentalHours || 0} Hours / ${booking.rentalKm || 0} KM` ||
                                        "Rental Service"}
                                  </div>
                                  <div className="text-xs text-gray-500 mt-1">
                                    City: {booking.pickupLocation?.name || booking.from || "Not specified"}
                                  </div>
                                </div>
                              ) : (
                                <>
                                  {booking.pickupLocation?.name ||
                                    booking.from ||
                                    "Not specified"}
                                  {booking.pickupLocation?.state &&
                                    `, ${booking.pickupLocation.state}`}
                                  {booking.pickupAddressDetails?.pincode &&
                                    ` ${booking.pickupAddressDetails.pincode}`}
                                  <span className="mx-2 text-gray-400 font-bold">
                                    →
                                  </span>
                                  {booking.dropLocation?.name ||
                                    booking.to ||
                                    "Not specified"}
                                  {booking.dropLocation?.state &&
                                    `, ${booking.dropLocation.state}`}
                                  {booking.dropAddressDetails?.pincode &&
                                    ` ${booking.dropAddressDetails.pincode}`}
                                </>
                              )}
                            </div>
                          </div>
                          {(() => {
                            // Enhanced pickup address display
                            const pickupLocationName =
                              booking.pickupLocation?.name || booking.from;
                            const pickupAddr = booking.pickupAddress;
                            const pickupLandmark =
                              booking.pickupAddressDetails?.landmark;
                            const pickupPincode = booking.pickupAddressDetails?.pincode;

                            // Always show detailed address if available
                            const hasDetailedAddress = pickupAddr || pickupLandmark || pickupPincode;

                            return hasDetailedAddress ? (
                              <div className="text-xs bg-blue-50 p-2 rounded border border-blue-200 mt-1">
                                <span className="font-medium text-blue-800">
                                  📍 Pickup Location Details:
                                </span>
                                <div className="mt-1 space-y-1">
                                  <div className="font-medium text-gray-900">
                                    {pickupLocationName}
                                  </div>
                                  {pickupAddr && pickupAddr !== pickupLocationName && (
                                    <div className="text-gray-700">
                                      <span className="font-medium">Address:</span> {pickupAddr}
                                    </div>
                                  )}
                                  {pickupLandmark && (
                                    <div className="text-gray-700">
                                      <span className="font-medium">Landmark:</span> {pickupLandmark}
                                    </div>
                                  )}
                                  {pickupPincode && (
                                    <div className="text-gray-700">
                                      <span className="font-medium">Pincode:</span> {pickupPincode}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <div className="text-xs text-gray-400 italic mt-1">
                                No detailed pickup address available
                              </div>
                            );
                          })()}
                        </div>
                      </td>
                      <td>
                        <div className="space-y-1">
                          {(() => {
                            // Enhanced drop address display
                            const dropLocationName =
                              booking.dropLocation?.name || booking.to;
                            const dropAddr = booking.dropAddress;
                            const dropLandmark =
                              booking.dropAddressDetails?.landmark;
                            const dropPincode = booking.dropAddressDetails?.pincode;
                            const additionalInfo =
                              booking.dropAddressDetails?.additionalInfo;

                            // Always show detailed address if available
                            const hasDetailedAddress = dropAddr || dropLandmark || dropPincode || additionalInfo;

                            if (hasDetailedAddress) {
                              return (
                                <div className="text-xs bg-green-50 p-2 rounded border border-green-200">
                                  <span className="font-medium text-green-800">
                                    🏁 Drop Location Details:
                                  </span>
                                  <div className="mt-1 space-y-1">
                                    <div className="font-medium text-gray-900">
                                      {dropLocationName}
                                    </div>
                                    {dropAddr && dropAddr !== dropLocationName && (
                                      <div className="text-gray-700">
                                        <span className="font-medium">Address:</span> {dropAddr}
                                      </div>
                                    )}
                                    {dropLandmark && (
                                      <div className="text-gray-700">
                                        <span className="font-medium">Landmark:</span> {dropLandmark}
                                      </div>
                                    )}
                                    {dropPincode && (
                                      <div className="text-gray-700">
                                        <span className="font-medium">Pincode:</span> {dropPincode}
                                      </div>
                                    )}
                                    {additionalInfo && (
                                      <div className="text-gray-700">
                                        <span className="font-medium">Additional Info:</span> {additionalInfo}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            } else if (booking.serviceType === "rental") {
                              return (
                                <div className="text-xs text-gray-400 italic">
                                  Rental service - drop location same as pickup
                                </div>
                              );
                            } else {
                              return (
                                <div className="text-xs text-gray-400 italic">
                                  No detailed drop address available
                                </div>
                              );
                            }
                          })()}
                        </div>
                      </td>
                      <td>
                        <div className="space-y-1">
                          <div className="text-sm font-bold text-purple-700 bg-purple-50 px-2 py-1 rounded">
                            🚗 {booking.cabType?.name ||
                              booking.cabType ||
                              booking.cabName ||
                              "Standard Cab"}
                          </div>

                          {/* Trip Type Information */}
                          <div className={`text-xs px-2 py-1 rounded font-medium ${
                            booking.serviceType === "airport"
                              ? "bg-orange-100 text-orange-800"
                              : booking.tripType === "roundTrip"
                              ? "bg-purple-100 text-purple-800"
                              : booking.tripType === "multiCity" || (booking.viaCities && booking.viaCities.length > 0)
                              ? "bg-green-100 text-green-800"
                              : "bg-blue-100 text-blue-800"
                          }`}>
                            {(() => {
                              // Handle airport transfer service type
                              if (booking.serviceType === "airport") {
                                if (booking.transferDirection === "to" || booking.airportDirection === "to") {
                                  return "��️ To Airport";
                                } else if (booking.transferDirection === "from" || booking.airportDirection === "from") {
                                  return "🛬 From Airport";
                                } else {
                                  return "✈️ Airport Transfer";
                                }
                              }
                              // Handle rental service type
                              else if (booking.serviceType === "rental") {
                                return "����️ Local Rental";
                              }
                              // Handle regular trip types
                              else if (booking.tripType === "roundTrip") {
                                return "🔄 Round Trip";
                              } else if (booking.tripType === "multiCity" || (booking.viaCities && booking.viaCities.length > 0)) {
                                const viaCities = booking.viaCities || [];
                                return viaCities.length > 0
                                  ? `🌐 Multi-City (via ${viaCities.join(", ")})`
                                  : "🌐 Multi-City";
                              } else {
                                return "➡️ One Way";
                              }
                            })()}
                          </div>

                          {/* Return Date for Round Trips */}
                          {booking.tripType === "roundTrip" && booking.returnDate && (
                            <div className="text-xs text-purple-600 bg-purple-50 px-2 py-1 rounded">
                              Return: {booking.returnDate}
                              {booking.returnTime && ` at ${booking.returnTime}`}
                            </div>
                          )}

                          {booking.serviceType === "rental" && (
                            <div className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
                              📦 {booking.packageName || "Rental Package"}
                              {booking.rentalHours && (
                                <div className="text-xs text-gray-600 mt-1">
                                  {booking.rentalHours} Hours
                                  {booking.rentalKm && ` / ${booking.rentalKm} KM`}
                                </div>
                              )}
                              {booking.extraFarePerHour && (
                                <div className="text-xs text-orange-600 mt-1">
                                  Extra Fare: ₹{booking.extraFarePerHour}/Hour
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </td>

                      <td>
                        <div className="flex flex-col items-start space-y-1">
                          <div className="font-semibold text-gray-900">
                            ₹{booking.amount || 0}
                          </div>
                          <div className="text-xs text-gray-500">
                            {booking.paymentMethod || "Cash"}
                          </div>
                          {booking.updatedAt && (
                            <span className="text-xs text-gray-400">
                              Updated{" "}
                              {booking.updatedAt
                                ? new Date(
                                    booking.updatedAt,
                                  ).toLocaleTimeString()
                                : "N/A"}
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="status-dropdown-container flex flex-col space-y-2" style={{position: 'relative', zIndex: 1}}>
                          {/* Current Status Display */}
                          <div className="text-center">
                            <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                              booking.status === 'completed' ? 'bg-green-100 text-green-800' :
                              booking.status === 'confirmed' ? 'bg-blue-100 text-blue-800' :
                              booking.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              booking.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                              {booking.status === 'completed' && <FaStar className="inline ml-1 h-3 w-3 text-yellow-500" title="Ratings available" />}
                              {booking.driver && (booking.status === 'confirmed' || booking.status === 'assigned') && (
                                <FaUserTie className="inline ml-1 h-3 w-3 text-blue-600" title="Driver assigned" />
                              )}
                            </span>
                          </div>

                          {/* Status Change Dropdown */}
                          <div className="relative" style={{zIndex: 10}}>
                            <select
                              className={`admin-dropdown w-full text-sm py-2 px-3 min-h-[40px] ${
                                updatingBookings.has(booking._id) ||
                                booking.status === "completed" ||
                                booking.status === "cancelled"
                                  ? "opacity-50 cursor-not-allowed"
                                  : ""
                              }`}
                              style={{fontSize: '14px', lineHeight: '1.4'}}
                              value=""
                              disabled={
                                updatingBookings.has(booking._id) ||
                                booking.status === "completed" ||
                                booking.status === "cancelled"
                              }
                              onChange={(e) => {
                                if (e.target.value) {
                                  handleStatusChange(booking._id, e.target.value);
                                  e.target.value = ""; // Reset dropdown
                                }
                              }}
                              title={
                                booking.status === "completed" ||
                                booking.status === "cancelled"
                                  ? `Cannot modify ${booking.status} booking status`
                                  : "Change booking status"
                              }
                            >
                              <option value="">
                                {booking.status === "completed" || booking.status === "cancelled"
                                  ? "Status Locked"
                                  : "Change to..."}
                              </option>
                              {/* Only allow pending → confirmed, not confirmed → pending */}
                              {booking.status === 'pending' && (
                                <option value="confirmed">✓ Mark as Confirmed</option>
                              )}
                              {/* Allow confirmed/assigned/inProgress → completed */}
                              {(booking.status === 'confirmed' || booking.status === 'assigned' || booking.status === 'inProgress') && (
                                <option value="completed">✓ Mark as Completed</option>
                              )}
                              {/* Allow cancellation from pending, confirmed, or assigned (but not completed) */}
                              {(booking.status === 'pending' || booking.status === 'confirmed' || booking.status === 'assigned') && (
                                <option value="cancelled">✗ Cancel Booking</option>
                              )}
                            </select>

                            {updatingBookings.has(booking._id) && (
                              <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 rounded-md z-10">
                                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-500"></div>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="space-y-2">
                          {booking.driver ? (
                            <>
                              <div className="flex items-center text-sm">
                                <FaUserTie className="mr-2 text-blue-600 flex-shrink-0" />
                                <div className="min-w-0 flex-1">
                                  <div className="font-medium text-gray-900 truncate">
                                    {((booking.driver.user && booking.driver.user.name) || booking.driver.name || "Unknown")}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {((booking.driver.user && booking.driver.user.phone) || booking.driver.phone || "")}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center text-sm">
                                <FaCar className="mr-2 text-purple-600 flex-shrink-0" />
                                <span className="text-purple-700 font-medium text-xs">
                                  {booking.cabType?.name || booking.cabType || booking.cabName || "Standard Cab"}
                                </span>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="flex items-center text-sm">
                                <FaCar className="mr-2 text-purple-600 flex-shrink-0" />
                                <span className="text-purple-700 font-medium text-xs">
                                  {booking.cabType?.name || booking.cabType || booking.cabName || "Standard Cab"}
                                </span>
                              </div>
                              <div className="text-gray-500 text-xs italic">
                                No driver assigned
                              </div>
                            </>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="flex justify-end items-center min-h-[40px]">
                          <div className="flex flex-col space-y-1 items-end">
                            {/* Rating View Button - Available for all bookings */}
                            <button
                              onClick={() => viewRatings(booking)}
                              className={`text-xs font-medium px-3 py-1 rounded-md transition-colors flex items-center ${
                                booking.status === 'completed'
                                  ? 'text-yellow-700 bg-yellow-50 hover:bg-yellow-100 border border-yellow-200'
                                  : 'text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100'
                              }`}
                              title={booking.status === 'completed' ? 'View customer and driver ratings' : 'Check ratings (available after completion)'}
                            >
                              {booking.status === 'completed' ? <FaStar className="mr-1" /> : <FaEye className="mr-1" />}
                              View Ratings
                              {booking.status === 'completed' && (
                                <span className="ml-1 bg-yellow-200 text-yellow-800 rounded-full px-1.5 py-0.5 text-xs">
                                  ✓
                                </span>
                              )}
                            </button>

                            {/* Driver-specific actions */}
                            {booking.driver ? (
                              <>
                                {/* Only show Remove Driver button for non-final states */}
                                {booking.status !== "completed" &&
                                booking.status !== "cancelled" ? (
                                  <button
                                    onClick={() => removeDriver(booking._id)}
                                    className="text-xs text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 px-3 py-1 rounded-md transition-colors font-medium"
                                  >
                                    Remove Driver
                                  </button>
                                ) : null}
                              </>
                            ) : booking.status === "confirmed" ? (
                              <button
                                onClick={() => openDriverAssignModal(booking)}
                                className="bg-green-600 text-white px-4 py-2 rounded-md text-sm hover:bg-green-700 transition-colors font-medium"
                              >
                                Assign Driver
                              </button>
                            ) : (
                              <span className="text-gray-500 text-sm text-center">
                                {booking.status === "pending"
                                  ? "Awaiting confirmation"
                                  : booking.status === "cancelled"
                                    ? "Booking cancelled"
                                    : "No actions available"}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      className="py-8 text-center text-gray-500"
                      colSpan="10"
                    >
                      {searchTerm || statusFilter !== "all"
                        ? "No bookings matching the criteria"
                        : "No bookings found"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalItems={totalFilteredBookings}
        itemsPerPage={itemsPerPage}
        onPageChange={setCurrentPage}
      />
      {/* Driver Assignment Modal */}
      {showDriverModal && (
        <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl">
            <div className="border-b px-6 py-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium">
                  Assign Driver to Booking
                </h3>
                <button
                  onClick={closeDriverAssignModal}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <div className="px-6 py-4">
              <div className="mb-4 bg-blue-50 p-3 rounded-lg">
                <h4 className="font-medium mb-2">Booking Details</h4>
                <div className="grid grid-cols-1 gap-3 text-sm">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <span className="text-gray-600">Booking ID:</span>{" "}
                      {selectedBooking?._id}
                    </div>
                    <div>
                      <span className="text-gray-600">Customer:</span>{" "}
                      {selectedBooking?.user.name}
                    </div>
                  </div>

                  <div className="border-t pt-2">
                    <div className="mb-2">
                      <span className="text-gray-600 font-medium">Route:</span>
                      <div className="text-blue-600 font-medium">
                        {selectedBooking?.pickupLocation?.name ||
                          selectedBooking?.from ||
                          "Not specified"}
                        {selectedBooking?.pickupLocation?.state &&
                          `, ${selectedBooking.pickupLocation.state}`}
                        {selectedBooking?.pickupAddressDetails?.pincode &&
                          ` ${selectedBooking.pickupAddressDetails.pincode}`}
                        <span className="mx-2 text-gray-400">→</span>
                        {selectedBooking?.dropLocation?.name ||
                          selectedBooking?.to ||
                          "Not specified"}
                        {selectedBooking?.dropLocation?.state &&
                          `, ${selectedBooking.dropLocation.state}`}
                        {selectedBooking?.dropAddressDetails?.pincode &&
                          ` ${selectedBooking.dropAddressDetails.pincode}`}
                      </div>
                    </div>

                    {(() => {
                      // Only show pickup address if different from location
                      const pickupLocationName =
                        selectedBooking?.pickupLocation?.name ||
                        selectedBooking?.from;
                      const pickupAddr = selectedBooking?.pickupAddress;
                      const pickupLandmark =
                        selectedBooking?.pickupAddressDetails?.landmark;

                      return pickupAddr &&
                        (pickupAddr !== pickupLocationName ||
                          pickupLandmark) ? (
                        <div className="mb-1">
                          <span className="text-gray-600 font-medium">
                            Pickup Address:
                          </span>
                          <div className="text-gray-700">
                            {pickupAddr}
                            {pickupLandmark && `, Near ${pickupLandmark}`}
                          </div>
                        </div>
                      ) : null;
                    })()}

                    {(() => {
                      // Only show drop address if different from location
                      const dropLocationName =
                        selectedBooking?.dropLocation?.name ||
                        selectedBooking?.to;
                      const dropAddr = selectedBooking?.dropAddress;
                      const dropLandmark =
                        selectedBooking?.dropAddressDetails?.landmark;

                      return dropAddr &&
                        (dropAddr !== dropLocationName || dropLandmark) ? (
                        <div className="mb-1">
                          <span className="text-gray-600 font-medium">
                            Drop Address:
                          </span>
                          <div className="text-gray-700">
                            {dropAddr}
                            {dropLandmark && `, Near ${dropLandmark}`}
                          </div>
                        </div>
                      ) : null;
                    })()}
                  </div>

                  <div className="grid grid-cols-2 gap-2 border-t pt-2">
                    <div>
                      <span className="text-gray-600">Date:</span>{" "}
                      {selectedBooking?.date}
                    </div>
                    <div>
                      <span className="text-gray-600">Cab Type:</span>{" "}
                      {selectedBooking?.cabType?.name ||
                        selectedBooking?.cabType ||
                        selectedBooking?.cabName ||
                        "Standard Cab"}
                    </div>
                    <div>
                      <span className="text-gray-600">Amount:</span> ₹
                      {selectedBooking?.amount || selectedBooking?.totalAmount || 0}
                    </div>
                    <div>
                      <span className="text-gray-600">Payment:</span>{" "}
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${getPaymentMethodBadgeClass(selectedBooking)}`}
                      >
                        {selectedBooking?.paymentMethod === "ONLINE"
                          ? "💳 Online"
                          : "💰 COD"}
                      </span>
                    </div>
                    {selectedBooking?.paymentId && selectedBooking?.paymentMethod === "ONLINE" && (
                      <div className="mt-2">
                        <span className="text-gray-600">Payment ID:</span>{" "}
                        <span className="text-sm font-mono text-blue-600 bg-blue-50 px-2 py-1 rounded">
                          {selectedBooking.paymentId}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Trip Type Information */}
                  <div className="border-t pt-3 mt-3">
                    <div className="mb-2">
                      <span className="text-gray-600 font-medium">Trip Type:</span>
                      <div className={`inline-block ml-2 px-3 py-1 rounded text-sm font-medium ${
                        selectedBooking?.tripType === "roundTrip"
                          ? "bg-purple-100 text-purple-800"
                          : selectedBooking?.tripType === "multiCity" || (selectedBooking?.viaCities && selectedBooking?.viaCities.length > 0)
                          ? "bg-green-100 text-green-800"
                          : "bg-blue-100 text-blue-800"
                      }`}>
                        {(() => {
                          if (selectedBooking?.tripType === "roundTrip") {
                            return "🔄 Round Trip";
                          } else if (selectedBooking?.tripType === "multiCity" || (selectedBooking?.viaCities && selectedBooking?.viaCities.length > 0)) {
                            const viaCities = selectedBooking?.viaCities || [];
                            return viaCities.length > 0
                              ? `��� Multi-City (via ${viaCities.join(", ")})`
                              : "���� Multi-City";
                          } else {
                            return "➡️ One Way";
                          }
                        })()}
                      </div>
                    </div>

                    {/* Return Date Information for Round Trips */}
                    {selectedBooking?.tripType === "roundTrip" && (selectedBooking?.returnDate || selectedBooking?.returnTime) && (
                      <div className="mt-2 p-2 bg-purple-50 border border-purple-200 rounded">
                        <span className="text-purple-800 font-medium text-sm">Return Journey:</span>
                        <div className="text-purple-700 text-sm mt-1">
                          {selectedBooking.returnDate && `Date: ${selectedBooking.returnDate}`}
                          {selectedBooking.returnTime && ` at ${selectedBooking.returnTime}`}
                        </div>
                      </div>
                    )}

                    {/* Via Cities Information for Multi-City */}
                    {(selectedBooking?.tripType === "multiCity" || (selectedBooking?.viaCities && selectedBooking?.viaCities.length > 0)) && (
                      <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded">
                        <span className="text-green-800 font-medium text-sm">Via Cities:</span>
                        <div className="text-green-700 text-sm mt-1">
                          {selectedBooking?.viaCities && selectedBooking.viaCities.length > 0
                            ? selectedBooking.viaCities.join(" → ")
                            : "Multi-city route details"}
                        </div>
                      </div>
                    )}
                  </div>
                  {selectedBooking?.paymentMethod === "ONLINE" && (
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center text-blue-800">
                        <FaWallet className="w-4 h-4 mr-2" />
                        <span className="font-medium text-sm">
                          Commission Check Required
                        </span>
                      </div>
                      <p className="text-xs text-blue-600 mt-1">
                        For COD bookings, driver wallet balance will be checked
                        to ensure commission can be deducted on completion.
                      </p>
                    </div>
                  )}

                  {/* Package Details Section */}
                  {selectedBooking?.serviceType === "rental" && (
                    <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center text-green-800 mb-2">
                        <FaClock className="w-4 h-4 mr-2" />
                        <span className="font-medium text-sm">Rental Package Details</span>
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-xs">
                        <div>
                          <span className="text-gray-600 font-medium">Package:</span>
                          <div className="text-green-700 font-semibold">
                            📦 {selectedBooking?.packageName ||
                                `${selectedBooking?.rentalHours || 0} Hours / ${selectedBooking?.rentalKm || 0} KM`}
                          </div>
                        </div>
                        <div>
                          <span className="text-gray-600 font-medium">Service City:</span>
                          <div className="text-gray-800">
                            {selectedBooking?.pickupLocation?.name ||
                             selectedBooking?.from || "Not specified"}
                          </div>
                        </div>
                        {/* Only show individual duration/distance if packageName doesn't already contain this info */}
                        {selectedBooking?.rentalHours && !selectedBooking?.packageName && (
                          <div>
                            <span className="text-gray-600 font-medium">Duration:</span>
                            <div className="text-gray-800">
                              🕒 {selectedBooking.rentalHours} Hours
                            </div>
                          </div>
                        )}
                        {selectedBooking?.rentalKm && !selectedBooking?.packageName && (
                          <div>
                            <span className="text-gray-600 font-medium">Distance Included:</span>
                            <div className="text-gray-800">
                              🛣️ {selectedBooking.rentalKm} KM
                            </div>
                          </div>
                        )}
                        {selectedBooking?.extraFarePerHour && (
                          <div className="col-span-2">
                            <span className="text-gray-600 font-medium">Extra Fare Rate:</span>
                            <div className="text-orange-600 font-semibold">
                              💰 ₹{selectedBooking.extraFarePerHour}/Hour
                              <span className="text-xs text-gray-500 ml-1">
                                (for hours beyond package limit)
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="mt-2 p-2 bg-green-100 rounded text-xs text-green-700">
                        <strong>Note:</strong> This is a rental service booking. Driver should wait at pickup location during the rental period.
                      </div>
                    </div>
                  )}

                  {/* Airport Transfer Details */}
                  {selectedBooking?.serviceType === "airport" && (
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center text-blue-800 mb-2">
                        <FaCar className="w-4 h-4 mr-2" />
                        <span className="font-medium text-sm">Airport Transfer Details</span>
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-xs">
                        {selectedBooking?.airportCode && (
                          <div>
                            <span className="text-gray-600 font-medium">Airport:</span>
                            <div className="text-blue-700 font-semibold">
                              ✈️ {selectedBooking.airportCode}
                            </div>
                          </div>
                        )}
                        <div>
                          <span className="text-gray-600 font-medium">Transfer Type:</span>
                          <div className="text-gray-800">
                            {selectedBooking?.journeyType === "airport_pickup" ? "🛬 Airport Pickup" :
                             selectedBooking?.journeyType === "airport_drop" ? "🛫 Airport Drop" :
                             "🔄 Airport Transfer"}
                          </div>
                        </div>
                      </div>
                      <div className="mt-2 p-2 bg-blue-100 rounded text-xs text-blue-700">
                        <strong>Note:</strong> Airport transfer service. Please ensure timely pickup/drop as per flight schedule.
                      </div>
                    </div>
                  )}

                  {/* Outstation Trip Details */}
                  {(selectedBooking?.serviceType === "outstation" || !selectedBooking?.serviceType) && (
                    <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                      <div className="flex items-center text-purple-800 mb-2">
                        <FaRoute className="w-4 h-4 mr-2" />
                        <span className="font-medium text-sm">Outstation Trip Details</span>
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-xs">
                        {selectedBooking?.distance && (
                          <div>
                            <span className="text-gray-600 font-medium">Distance:</span>
                            <div className="text-purple-700 font-semibold">
                              ��️ {Math.round(selectedBooking.distance)} KM
                            </div>
                          </div>
                        )}
                        {selectedBooking?.duration && (
                          <div>
                            <span className="text-gray-600 font-medium">Duration:</span>
                            <div className="text-purple-700 font-semibold">
                              ⏱️ {selectedBooking.duration}
                            </div>
                          </div>
                        )}
                        {selectedBooking?.journeyType && (
                          <div className="col-span-2">
                            <span className="text-gray-600 font-medium">Journey Type:</span>
                            <div className="text-gray-800">
                              {selectedBooking.journeyType === "round_trip" ? "���� Round Trip" :
                               selectedBooking.journeyType === "one_way" ? "➡️ One Way" :
                               selectedBooking.journeyType}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="mt-2 p-2 bg-purple-100 rounded text-xs text-purple-700">
                        <strong>Note:</strong> Outstation trip. Driver allowances and toll charges may apply.
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Assignment Type Tabs */}
              <div className="flex border-b mb-4">
                <button
                  onClick={() => {
                    console.log('Switching to driver tab');
                    setAssignmentType("driver");
                  }}
                  className={`px-4 py-2 font-medium text-sm ${
                    assignmentType === "driver"
                      ? "border-b-2 border-blue-500 text-blue-600"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Assign Driver
                </button>
                <button
                  onClick={() => {
                    console.log('Switching to vendor tab');
                    console.log('Vendors available:', vendors.length);
                    setAssignmentType("vendor");
                    // Force refresh vendors when switching to vendor tab
                    if (vendors.length === 0) {
                      fetchVendorsManually();
                    }
                  }}
                  className={`px-4 py-2 font-medium text-sm ${
                    assignmentType === "vendor"
                      ? "border-b-2 border-blue-500 text-blue-600"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Assign Vendor
                </button>
              </div>

              {assignmentType === "driver" && (
                <>
                  <div className="mb-2 p-2 bg-blue-50 border border-blue-200 rounded">
                    <h4 className="font-medium text-blue-800">
                      �� Admin Drivers for{" "}
                      {selectedBooking?.cabType?.name ||
                        selectedBooking?.cabType ||
                        selectedBooking?.cabName ||
                        "Standard Cab"}
                    </h4>
                    <p className="text-xs text-blue-600 mt-1">
                      Direct assignment to admin drivers
                    </p>
                  </div>
                </>
              )}

              {assignmentType === "vendor" && (
                <>
                  <div className="mb-2 p-2 bg-green-50 border border-green-200 rounded">
                    <h4 className="font-medium text-green-800">
                      🏢 Available Vendors
                    </h4>
                    <p className="text-xs text-green-600 mt-1">
                      Assign this booking to a vendor. The vendor will then assign one of their drivers through their dashboard.
                    </p>
                  </div>
                </>
              )}
              <div className="overflow-y-auto max-h-80">
                {/* Driver Assignment Content - Only show when driver tab is selected */}
                {assignmentType === "driver" && (
                  <table className="min-w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="py-2 px-3 text-left text-sm font-medium">
                          Driver & Contact
                        </th>
                        <th className="py-2 px-3 text-left text-sm font-medium">
                          Status & Wallet
                        </th>
                        <th className="py-2 px-3 text-left text-sm font-medium">
                          Car Type
                        </th>
                        <th className="py-2 px-3 text-left text-sm font-medium">
                          Action
                        </th>
                      </tr>
                    </thead>
                  <tbody className="divide-y divide-gray-200">
                    {/* Filter drivers by availability and matching cab type */}
                    {drivers
                      .filter((driver) => {
                        // Only show available drivers
                        if (driver.status !== "available") return false;

                        // Always allow a match in this case regardless of cab type
                        // This ensures drivers show up for the assignment demo purpose

                        // For debugging, log the comparison
                        const bookingCabType = (
                          selectedBooking?.cabType?.name ||
                          selectedBooking?.cabType ||
                          selectedBooking?.cabName ||
                          ""
                        )
                          .toLowerCase()
                          .trim();
                        const driverCabType = (driver.carType || "")
                          .toLowerCase()
                          .trim();

                        console.log(
                          `Comparing booking cab type: "${bookingCabType}" with driver cab type: "${driverCabType}"`,
                        );

                        // Use vehicle type ID to match with booking cab type
                        // This allows us to match by ID instead of hardcoded strings
                        const vehicleTypeMatch =
                          driver.vehicleTypeId &&
                          cabTypes[driver.vehicleTypeId] &&
                          cabTypes[driver.vehicleTypeId]
                            .toLowerCase()
                            .trim() === bookingCabType;

                        // Check if cab types match exactly by name
                        const exactMatch = bookingCabType === driverCabType;

                        // Log when we find a match
                        if (exactMatch || vehicleTypeMatch) {
                          console.log(
                            `\u2705 MATCH FOUND: ${driver.name}'s cab type "${driverCabType}" matches booking cab type "${bookingCabType}"`,
                          );
                        }

                        return exactMatch || vehicleTypeMatch;
                      })
                      .map((driver) => (
                        <tr key={driver.id} className="hover:bg-gray-50">
                          <td className="py-3 px-3">
                            <div className="flex items-center">
                              <FaUser className="text-gray-500 mr-2" />
                              <div className="font-medium">{driver.name} {driver.phone}</div>
                            </div>
                          </td>
                          <td className="py-3 px-3">
                            <div className="text-sm text-green-600 font-medium">Available</div>
                            {/* Always show wallet status */}
                            <WalletStatus
                              driverId={driver._id || driver.id}
                              bookingAmount={getBookingAmount(selectedBooking)}
                              user={user}
                              API_URL={API_URL}
                              paymentMethod={selectedBooking.paymentMethod}
                            />
                          </td>
                          <td className="py-3 px-3">
                            <div className="flex items-center">
                              <FaCar className="text-purple-500 mr-2" />
                              <div className="font-medium text-purple-700">
                                {driver.vehicleType
                                  ? typeof driver.vehicleType === "object"
                                    ? `${driver.vehicleType.name} ${driver.vehicleType.acAvailable ? "(AC)" : "(Non-AC)"}`
                                    : driver.vehicleType
                                  : driver.carType || "Unknown Type"}
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-3">
                            <AssignButton
                              driver={driver}
                              selectedBooking={selectedBooking}
                              assignDriver={assignDriver}
                              user={user}
                              API_URL={API_URL}
                            />
                          </td>
                        </tr>
                      ))}
                    {drivers.filter((driver) => {
                      if (driver.status !== "available") return false;

                      // Use the same matching logic as above
                      const bookingCabType = (
                        selectedBooking?.cabType?.name ||
                        selectedBooking?.cabType ||
                        selectedBooking?.cabName ||
                        ""
                      )
                        .toLowerCase()
                        .trim();
                      const driverCabType = (driver.carType || "")
                        .toLowerCase()
                        .trim();

                      // Match based on vehicle type ID mapping from database
                      const vehicleTypeMatch =
                        driver.vehicleTypeId &&
                        cabTypes[driver.vehicleTypeId] &&
                        cabTypes[driver.vehicleTypeId].toLowerCase().trim() ===
                          bookingCabType;

                      // Exact match by name
                      const exactMatch = bookingCabType === driverCabType;

                      return exactMatch || vehicleTypeMatch;
                    }).length === 0 && (
                      <tr>
                        <td colSpan="4" className="py-6 text-center">
                          <div className="flex flex-col items-center">
                            <FaCar className="text-gray-400 text-3xl mb-2" />
                            <p className="text-gray-500 font-medium">
                              No available admin drivers for{" "}
                              {selectedBooking?.cabType?.name ||
                                selectedBooking?.cabType ||
                                selectedBooking?.cabName ||
                                "Standard Cab"}{" "}
                              cab type
                            </p>
                            <p className="text-gray-400 text-sm mt-1">
                              All admin drivers are assigned, or only vendor drivers are available.
                              Try the "Assign Vendor" tab.
                            </p>
                          </div>
                        </td>
                      </tr>
                    )}
                    </tbody>
                  </table>
                )}

                {/* Vendor Assignment Content - Only show when vendor tab is selected */}
                {assignmentType === "vendor" && (
                  <table className="min-w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="py-2 px-3 text-left text-sm font-medium">
                          Vendor & Contact
                        </th>
                        <th className="py-2 px-3 text-left text-sm font-medium">
                          Status & Details
                        </th>
                        <th className="py-2 px-3 text-left text-sm font-medium">
                          Drivers & Wallet
                        </th>
                        <th className="py-2 px-3 text-left text-sm font-medium">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {vendors.length === 0 && (
                        <tr>
                          <td colSpan="4" className="py-6 text-center">
                            <div className="flex flex-col items-center">
                              <FaBuilding className="text-gray-400 text-3xl mb-2" />
                              <div className="text-gray-500">
                                Loading vendors... ({vendors.length} vendors found)
                              </div>
                              <button
                                onClick={fetchVendorsManually}
                                className="mt-2 px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                              >
                                Retry Loading Vendors
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                      {vendors
                        .filter((vendor) => vendor.verificationStatus === "approved")
                        .map((vendor) => (
                          <tr key={vendor._id} className="hover:bg-gray-50">
                            <td className="py-3 px-3">
                              <div className="flex items-center">
                                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                                  <FaBuilding className="text-blue-600 w-4 h-4" />
                                </div>
                                <div>
                                  <div className="font-medium text-gray-900">
                                    {vendor.fullName || vendor.name}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {vendor.email}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {vendor.phone}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="py-3 px-3">
                              <div className="text-sm">
                                <div className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                                  vendor.verificationStatus === "approved"
                                    ? "bg-green-100 text-green-800"
                                    : "bg-yellow-100 text-yellow-800"
                                }`}>
                                  {vendor.verificationStatus}
                                </div>
                                {vendor.businessName && (
                                  <div className="text-gray-600 mt-1">
                                    {vendor.businessName}
                                  </div>
                                )}
                                {vendor.city && (
                                  <div className="text-gray-500 text-xs mt-1">
                                    📍 {vendor.city}, {vendor.state}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-3">
                              <div className="text-sm text-gray-900">
                                {vendor.walletInfo?.totalDrivers || vendor.totalDrivers || 0} drivers
                              </div>
                              <div className="text-xs text-gray-500">
                                {vendor.walletInfo?.activeDrivers || 0} active
                              </div>
                              {vendor.walletInfo && (
                                <div className="mt-1 space-y-0.5">
                                  <div className="text-xs font-medium text-green-600">
                                    Total Wallet Balance: {formatCurrency(Number(vendorWalletMap[vendor._id] ?? 0))}
                                  </div>
                                  <div className="text-[11px] text-gray-500">
                                    Across {vendor.walletInfo.totalDrivers || 0} drivers
                                  </div>
                                  <div className="text-xs text-purple-600">
                                    {(() => {
                                      const rate = vendorCommissionMap[vendor._id]?.effectiveRate ??
                                                   vendor.commissionData?.commissionSettings?.effectiveRate ??
                                                   vendor.commissionData?.commissionStructure?.vendorRate ?? null;
                                      return rate != null ? `Commission Rate: ${rate}% per ride` : 'Commission Rate: N/A';
                                    })()}
                                  </div>
                                </div>
                              )}
                            </td>
                            <td className="py-3 px-3">
                              <button
                                onClick={() => assignVendor(vendor._id)}
                                className="bg-blue-600 text-white px-3 py-1 rounded-md text-sm hover:bg-blue-700 transition-colors"
                              >
                                Assign Vendor
                              </button>
                            </td>
                          </tr>
                        ))}
                      {vendors.filter((vendor) => vendor.verificationStatus === "approved").length === 0 && (
                        <tr>
                          <td colSpan="4" className="py-6 text-center">
                            <div className="flex flex-col items-center">
                              <FaBuilding className="text-gray-400 text-3xl mb-2" />
                              <p className="text-gray-500 font-medium">
                                No approved vendors available
                              </p>
                              <p className="text-gray-400 text-sm mt-1">
                                All vendors are either pending approval or assigned
                              </p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            <div className="bg-gray-50 px-6 py-4 flex justify-end">
              <button
                onClick={closeDriverAssignModal}
                className="bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Confirmation Modal */}
      <StatusConfirmationModal
        isOpen={showStatusModal}
        onClose={closeStatusModal}
        onConfirm={updateBookingStatus}
        booking={selectedBooking}
        newStatus={pendingStatusChange?.newStatus}
        isDriverControlled={
          selectedBooking &&
          (selectedBooking.status === "completed" ||
            selectedBooking.status === "cancelled")
        }
      />

      {/* Rating Viewer Modal */}
      {showRatingModal && (
        <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            <div className="border-b px-6 py-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium">Booking Ratings</h3>
                <button
                  onClick={() => setShowRatingModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <FaTimes className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="px-6 py-4 overflow-y-auto max-h-[70vh]">
              {selectedRatingData?.loading ? (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                  <span className="ml-3 text-gray-600">Loading ratings...</span>
                </div>
              ) : selectedRatingData?.error ? (
                <div className="text-center py-8">
                  <div className="text-red-500 mb-2">{selectedRatingData.error}</div>
                  <button
                    onClick={() => viewRatings(selectedRatingData.booking)}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    Try again
                  </button>
                </div>
              ) : selectedRatingData?.ratings ? (
                <div className="space-y-6">
                  {/* Booking Info */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium mb-2">Booking Details</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Booking ID:</span>
                        <div className="font-mono">{selectedRatingData.booking._id}</div>
                      </div>
                      <div>
                        <span className="text-gray-600">Customer:</span>
                        <div>{selectedRatingData.booking.user.name}</div>
                      </div>
                    </div>
                  </div>

                  {/* Customer Rating */}
                  {selectedRatingData.ratings.customerRating && (
                    <div className="border border-gray-200 p-4 rounded-lg">
                      <h5 className="font-medium mb-3 flex items-center">
                        <FaUser className="mr-2 text-blue-600" />
                        Customer Rating for Driver
                      </h5>
                      <div className="flex items-center mb-2">
                        <div className="flex items-center mr-3">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <FaStar
                              key={star}
                              className={`h-5 w-5 ${
                                star <= selectedRatingData.ratings.customerRating.rating
                                  ? "text-yellow-400"
                                  : "text-gray-300"
                              }`}
                            />
                          ))}
                        </div>
                        <span className="text-lg font-semibold">
                          {selectedRatingData.ratings.customerRating.rating}/5
                        </span>
                      </div>
                      {selectedRatingData.ratings.customerRating.comment && (
                        <div className="mt-3">
                          <span className="text-gray-600 text-sm">Comment:</span>
                          <p className="mt-1 text-gray-800 italic">
                            "{selectedRatingData.ratings.customerRating.comment}"
                          </p>
                        </div>
                      )}
                      <div className="mt-2 text-xs text-gray-500">
                        Rated on: {new Date(selectedRatingData.ratings.customerRating.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  )}

                  {/* Driver Rating */}
                  {selectedRatingData.ratings.driverRating && (
                    <div className="border border-gray-200 p-4 rounded-lg">
                      <h5 className="font-medium mb-3 flex items-center">
                        <FaUserTie className="mr-2 text-green-600" />
                        Driver Rating for Customer
                      </h5>
                      <div className="flex items-center mb-2">
                        <div className="flex items-center mr-3">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <FaStar
                              key={star}
                              className={`h-5 w-5 ${
                                star <= selectedRatingData.ratings.driverRating.rating
                                  ? "text-yellow-400"
                                  : "text-gray-300"
                              }`}
                            />
                          ))}
                        </div>
                        <span className="text-lg font-semibold">
                          {selectedRatingData.ratings.driverRating.rating}/5
                        </span>
                      </div>
                      {selectedRatingData.ratings.driverRating.comment && (
                        <div className="mt-3">
                          <span className="text-gray-600 text-sm">Comment:</span>
                          <p className="mt-1 text-gray-800 italic">
                            "{selectedRatingData.ratings.driverRating.comment}"
                          </p>
                        </div>
                      )}
                      <div className="mt-2 text-xs text-gray-500">
                        Rated on: {new Date(selectedRatingData.ratings.driverRating.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  )}

                  {!selectedRatingData.ratings.customerRating && !selectedRatingData.ratings.driverRating && (
                    <div className="text-center py-8">
                      <FaStar className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">No ratings available for this booking yet.</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FaStar className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">
                    {selectedRatingData?.message || "No ratings available for this booking yet."}
                  </p>
                </div>
              )}
            </div>

            <div className="bg-gray-50 px-6 py-4 flex justify-end">
              <button
                onClick={() => setShowRatingModal(false)}
                className="bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Bookings;
